import { createContext, Fragment, useContext, useEffect, useId, useRef, useState } from 'react';
import type { JSX, ReactNode, Ref } from 'react';
import { cn } from '../lib/cn.js';
import { useLoadingState } from '../lib/useLoadingState.js';
import { Button } from './Button.js';
import { SkeletonListRows } from './Skeleton.js';
import { ToolPopover } from './ToolPopover.js';
import type { ToolPopoverItem } from './ToolPopover.js';
import {
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconClose,
  IconFilter2,
  IconLineArrowTransferHorizontal1,
  IconPlus,
  IconSearch,
} from '../icons/index.js';
// LES GLYPHES DU FONDATEUR (dossier `icons/custom/`, dessines par lui, ecrits
// a la main). Le prototype valide du 07/08/2026 rend la rangee 3 et le tiroir
// avec CEUX-LA, pas avec des Streamline : l'etoile simple ouvre le tiroir (3b),
// l'etoile a PLUS en fabrique un (4b), et les fleches, le partage et le "..."
// tiennent la rangee 3c au niveau fiche. Ils vivent hors de `icons/generated/`
// (efface a chaque regeneration du set) et leur viewBox n'est pas 24 : le
// pourquoi tient dans l'entete de `../icons/custom/index.ts`.
import {
  IconArchive,
  IconFav,
  IconFavAddBigBig,
  IconFullSpaced,
  IconLeft,
  IconLeftBack,
  IconRight,
  IconShareFile,
} from '../icons/custom/index.js';
import { ConfirmDialog, ArchiveConfirmDialog, RemoveConfirmDialog } from './ConfirmDialog.js';
// LES FENETRES DU LOT (chantier 3_0). Elles sont ECRITES ailleurs et MONTEES
// ici, a la racine de l'arbre rendu : la rangee 3 est `relative z-10`, donc
// elle ouvre un contexte d'empilement dont un overlay ne sortirait plus. Le
// piege a deja ete paye trois fois dans ce pack (les poignees de colonne, la
// colonne de choix, et cette barre) ; il ne se repaie pas ici.
// Les deux fenetres de CONFIRMATION, elles, sont celles du chantier 2_0
// (`ConfirmDialog.tsx`), reprises telles quelles avec un NOMBRE : il n'existe
// pas de troisieme forme de confirmation dans ce produit.
import { BulkEditDialog } from './BulkEditDialog.js';
// `BULK_MAX` EST IMPORTE, JAMAIS RECOPIE. Le plafond appartient au moteur de
// lot ; le chassis, lui, doit l'ANNONCER avant d'ouvrir quoi que ce soit (voir
// `bulkAvailability` dans le corps du composant). Deux copies du meme nombre
// divergeraient au premier ajustement, et la barre laisserait alors passer un
// lot que la fenetre refuserait APRES la confirmation destructrice.
import { BulkRunner, BULK_MAX } from './BulkRunner.js';
import { ViewSelector } from '../views/ViewSelector.js';
import type {
  BulkEditField,
  Cursor,
  ProjectedRow,
  SaveState,
  ScreenFavorite,
  SortLevel,
  ViewId,
  ViewOption,
} from '../views/contract.js';

/**
 * Navigation fournie par le shell a l'ecran d'app (contexte, pour ne pas cabler
 * chaque app). `onBack` : si fourni, la puce d'icone de l'app (zone 1a) devient
 * un bouton retour (l'icone se change en fleche au survol) ; le shell y renvoie
 * vers la derniere app visitee, sinon vers le home. `backLabel` : libelle du
 * tooltip natif du bouton (ex. "Retour a Contact", sinon "Home").
 *
 * A NE PAS CONFONDRE avec `ScreenLayoutProps.onRecordExit`, qui ferme seulement
 * la FICHE ouverte et vit dans la rangee 3. Ici, on quitte l'APP entiere. Dans
 * tout ce fichier, le mot "back" est reserve a cette sortie-la.
 */
export interface ScreenNav {
  onBack?: () => void;
  backLabel?: string;
}
const ScreenNavContext = createContext<ScreenNav>({});
export function ScreenNavProvider({
  value,
  children,
}: {
  value: ScreenNav;
  children: ReactNode;
}): JSX.Element {
  return <ScreenNavContext.Provider value={value}>{children}</ScreenNavContext.Provider>;
}

/**
 * Le placeholder de la recherche (2b) quand ni la porte ni l'ecran n'en
 * declarent. Aucune IA n'est cablee derriere : c'est un placeholder, la
 * promesse appartient au shell (Ask Taki AI), jamais a l'ecran. La constante
 * vit ICI (c'est le chassis qui la rend) ; `useScreen` la re-exporte pour la
 * resolution de porte.
 */
export const DEFAULT_SEARCH_PLACEHOLDER = 'Rechercher ou demander a Lia...';

/**
 * ScreenLayout : le gabarit d'ecran STANDARD des apps MyTaki (gabarit v2).
 *
 * REGLE D'OR (cage IA) : tout ecran d'app utilise ce gabarit. Mode d'emploi
 * complet : docs/GABARIT-ECRAN.md. Une app remplit des zones nommees via des
 * props structurees ; elle ne reconstruit JAMAIS ce squelette a la main.
 *
 * LE PLAN DES ZONES (dicte par le fondateur, 04/08/2026). Le dock (D) est au
 * shell, hors gabarit :
 *
 *   ┌──────────┬────────────────────────────────────────────────┐
 *   │ [] 1a    │ 2a Titre    2b Recherche & Lia       2c Stats  │
 *   │          ├────────────────────────────────────────────────┤
 *   │ 1b Menu  │ 3a Actions           3b Outils  3c Vues        │
 *   │  = les   ├────────────────────────────────────────────────┤
 *   │  PORTES  │ 4  Tiroir : 4a tags . 4b garder . 4c favoris   │
 *   │          ├────────────────────────────────────────────────┤
 *   │          │ 5  Contenu (seule zone qui defile)             │
 *   └──────────┴────────────────────────────────────────────────┘
 *
 * Correspondance V1 -> V2 (regle de stabilite : un nouvel emplacement est une
 * lettre de plus dans sa bande, jamais une renumerotation ; 5 ne change jamais
 * de sens) : 1=1 . 2=2a . 3=2c . 4=2b+3b+4 . 4bis=3c . 6=3a . 7=SUPPRIMEE.
 *
 * - Obligatoires : 2a (title) et 5 (children).
 * - Le rail gauche (1) est TOUJOURS rendu et S'ARRETE AU MENU : identite de
 *   l'app (puce + nom) puis les entrees. Plus aucune action n'y vit : la
 *   zone 6 d'avant est la rangee 3a, la zone 7 est supprimee.
 * - Zone 1b : chaque entree designe une PORTE, un JEU DE DONNEES de l'app
 *   (la zone 1 dit QUEL JEU, la zone 4 dit QUELLE TRANCHE). Libelle + compteur
 *   mono, jamais d'icone ; actif = surface-3 + encre + aria-current. Pour un
 *   ecran V1 sans portes, rien ne change : les entrees restent ses sections.
 * - En-tete (2) : grid 3 colonnes minmax(0,1fr) / minmax(9rem,22rem) /
 *   minmax(0,1fr). Les stats (2c) ne cedent jamais, la recherche (2b) cede
 *   jusqu'a 9rem, le titre (2a) tronque en dernier. Sans stat, la grille reste
 *   la meme : le titre ne bouge pas.
 * - Rangee 3 : les ACTIONS de la porte a gauche (3a), un vide au milieu, le
 *   groupe d'OUTILS (3b : filtre, tri, etoile) puis le groupe EPINGLE (3c). La
 *   rangee reste affichee dans TOUS les etats de la zone 5 ET aux deux niveaux.
 *   Le groupe epingle garde exactement sa place, seul son CONTENU suit le
 *   niveau : au niveau collection le SELECTEUR DE VUES (jamais rendu sous
 *   condition, et il ne porte QUE les vues de collection) ; au niveau fiche le
 *   CURSEUR de fiche, un filet, puis les gestes SUR la fiche, pendant que la
 *   partie gauche devient la puce de sortie + l'indicateur d'enregistrement.
 * - Zone 4 : le TIROIR (tags + garder + favoris), replie par defaut, ouvert
 *   par le moteur au premier tag et par l'etoile ensuite. Il n'existe qu'au
 *   niveau collection et que si la porte se tranche (`sections`), a UNE
 *   exception pres : sur une porte sans tranches, le MODE ARCHIVES le fait
 *   apparaitre deploye tant qu'il dure, pour porter le tag qui explique la
 *   liste, et le fait redisparaitre en s'eteignant.
 * - L'OUTIL FILTRE (3b) EST TOUJOURS RENDU depuis le chantier 2_0. Son
 *   catalogue n'est jamais vide : le socle y injecte, tout en bas et derriere
 *   un filet, l'entree "Archives" que toute porte de toute app offre. Sur une
 *   porte sans tranches, le popover ne montre QUE cette entree.
 * - Etats standard de la zone 5 (priorite) : error > loading > empty >
 *   children ; squelette temporise par useLoadingState. ECRAN A VUES NATIVES :
 *   ne passer NI loading, NI error, NI empty, le ViewHost tient la meme
 *   priorite dans la zone 5.
 * - Scroll : uniquement la zone 5 (et le menu 1b si tres long).
 * - Le parent (route d'app du shell) doit donner une hauteur : le gabarit
 *   occupe h-full.
 *
 * RETRO-COMPATIBILITE V1, non negociable : les ecrans d'avant les portes
 * compilent et fonctionnent SANS modification. Leurs declarations changent de
 * place, pas de sens : `quickActions` se rend en 3a (plus dans le rail),
 * `onSearchChange` se rend en 2b (plus dans l'ancienne zone 4), `filters` se
 * rend en pastilles a choix unique dans la rangee 3 (l'habit V1, remplace par
 * `tools` une fois l'ecran migre). La prop `fixedActions` (zone 7) est
 * SUPPRIMEE : le grep du chantier a confirme qu'aucune app ne l'utilisait.
 */

/**
 * Entree du menu de l'app (zone 1b) : une PORTE pour un ecran v2, une section
 * libre pour un ecran V1.
 *
 * Pas d'icone : l'identite visuelle de l'app vit a UN seul endroit, la puce
 * d'icone en tete du rail (zone 1a, prop `appIcon`), jamais repetee.
 */
export interface ScreenMenuItem {
  /** Id stable de l'entree (utilise par `activeMenuId` / `onMenuSelect`). */
  id: string;
  /** Libelle affiche. */
  label: string;
  /**
   * Compteur optionnel affiche a droite, en mono. Calcule sur le jeu COMPLET
   * de la porte et GELE : il ne bouge ni au filtrage ni a la recherche.
   */
  count?: number | string;
  /**
   * Icone optionnelle rendue a gauche du label. Usage EXCEPTIONNEL, reserve
   * au hub Parametres du shell : les apps ne mettent JAMAIS d'icone dans leur
   * menu (regle inchangee, l'identite visuelle vit dans la puce `appIcon`).
   */
  icon?: ReactNode;
}

/** Statistique de l'en-tete (zone 2c). */
export interface ScreenStat {
  /** Valeur mise en avant (ex. "128", "2 min"). */
  value: string;
  /** Libelle sous la valeur (ex. "contacts"). */
  label: string;
}

/**
 * Un filtre tel que le gabarit le rend : pastille V1 (prop `filters`) ou
 * entree du popover de l'outil filtre (prop `tools`, catalogue de la porte).
 * La forme est un superset de ce que `useScreen().filters` rend : un ecran le
 * passe tel quel des deux cotes.
 */
export interface ScreenFilter {
  /** Id stable du filtre. */
  id: string;
  /** Libelle affiche (pastille ou entree de popover). */
  label: string;
  /**
   * Combien de lignes de la porte BRUTE ce filtre retient (compteur gele,
   * regle "un compteur ne bouge pas quand on filtre"). Rendu en detail mono
   * dans le popover de l'outil filtre ; ignore par les pastilles V1.
   */
  count?: number;
}

/**
 * Une cle de tri proposee par l'ecran (popover de l'outil tri, zone 3b).
 * `key` est une cle de colonne ou de role, la MEME que celle des niveaux de
 * `sortStack` : c'est elle qui fait le pont avec l'en-tete du tableau.
 */
export interface ScreenSortOption {
  key: string;
  label: string;
}

/**
 * LA FENETRE QU'UNE ACTION DEMANDE AVANT DE SE FAIRE.
 *
 * Quand une `ScreenAction` porte un `confirm`, c'est LE CHASSIS qui ouvre la
 * fenetre et n'appelle `onClick` qu'APRES confirmation : l'appelant (une porte,
 * un ecran, le moteur) ne gere aucune fenetre et ne peut donc pas en oublier
 * une. C'est aussi ce qui garantit qu'un meme geste porte la meme fenetre d'un
 * bout a l'autre du produit.
 *
 * Les deux fenetres du produit se composent avec `archiveConfirm(n)` et
 * `removeConfirm(n)` (`ConfirmDialog.tsx`), qui tiennent les textes et l'accord
 * du nombre : n'ecris pas ces phrases a la main dans une app.
 */
export interface ScreenConfirm {
  /** `neutral` : rien de definitif. `danger` : la seule fenetre rouge du produit. */
  tone: 'neutral' | 'danger';
  /** La question, en tete ("Archiver cette fiche ?"). */
  title: string;
  /** Le corps ("Elle quitte les listes. Tu la retrouveras dans les archives."). */
  body: string;
  /** Libelle du bouton qui fait le geste ("Archiver"). */
  confirmLabel: string;
  /** Libelle de la sortie. Defaut "Annuler", qui porte le focus par defaut. */
  cancelLabel?: string;
}

/** Action de la rangee 3a. */
export interface ScreenAction {
  /** Id stable de l'action. */
  id: string;
  /**
   * Libelle du bouton. Plafond visuel : la rangee 3a est en h 2rem et ne coupe
   * jamais un libelle (G13) ; elle defile au-dela, le libelle complet reste en
   * title. Pour une action `kind: 'add'`, ce libelle est IGNORE (voir kind).
   */
  label: string;
  /**
   * Icone optionnelle (icone @taki/ui conseillee, size 15). Ignoree pour une
   * action `kind: 'add'` : l'icone d'ajout est IconPlus, figee (regle G5).
   */
  icon?: ReactNode;
  /**
   * LA REGLE "+ AJOUTER" (fondateur, 04/08/2026), encodee dans le socle : une
   * action declaree `kind: 'add'` rend son libelle UNIVERSEL elle-meme
   * ("Ajouter", jamais "Nouveau materiel" ni "Ajouter item"), en PREMIER, en
   * lime, icone IconPlus. L'app declare le geste, le gabarit impose l'habit.
   */
  kind?: 'add';
  /**
   * Variante visuelle. Par defaut, une action de la rangee 3a est SECONDAIRE :
   * surface blanche a leger contour (`--border`). Le proto v2d les voulait sans
   * cadre, le fondateur a corrige au premier essai (04/08) : un libelle sans
   * cadre se lit comme du texte pose dans le vide, pas comme un bouton.
   * `primary` = promotion en lime, possible sur decision motivee mais JAMAIS
   * deux limes sur un ecran :
   * s'il y a une action `kind: 'add'`, c'est elle le lime et toute autre
   * `primary` est rendue secondaire.
   */
  variant?: 'primary' | 'secondary';
  /** Callback au clic. */
  onClick: () => void;
  /** Action desactivee (grisee, non cliquable). */
  disabled?: boolean;
  /**
   * LA RAISON de la desactivation, rendue en `title` quand `disabled` (G16 et
   * G18 : un controle eteint dit POURQUOI, et il ne disparait jamais). Phrase
   * complete, pas un mot : "Cette porte ne sait pas supprimer une fiche."
   */
  disabledReason?: string;
  /**
   * LA FENETRE A OUVRIR AVANT D'AGIR. Presente : le chassis l'ouvre et n'appelle
   * `onClick` qu'apres confirmation. Absente : le clic agit tout de suite.
   *
   * Lue aujourd'hui pour les gestes du menu "..." (`recordMenuActions`). Une action
   * de la rangee 3a qui la porterait serait servie par le meme chemin.
   */
  confirm?: ScreenConfirm;
}

/**
 * L'ETAT DES OUTILS ET DU TIROIR (zones 3b + 4), tel que le moteur le tient.
 *
 * La forme est calquee sur `useScreen()` : un ecran v2 passe son etat TEL QUEL
 * (`tools={screen}`), rien a recabler champ par champ ; le surplus de l'etat
 * d'ecran est ignore. Tout est optionnel : un champ absent eteint le geste
 * correspondant (un ecran sans favoris ne rend ni etoile ni tiroir de
 * favoris). Le gabarit ne DECIDE rien ici : l'ouverture du tiroir au premier
 * tag, la remise a zero au changement de porte et la correspondance exacte
 * d'un favori vivent dans le moteur.
 */
export interface ScreenToolset {
  /**
   * Le catalogue de filtres de la porte active (popover de l'outil filtre).
   * `useScreen().filters` s'y passe tel quel (les compteurs suivent).
   */
  filters?: ScreenFilter[];
  /** Les filtres actifs, dans l'ordre de pose : les tags 4a. */
  activeFilterIds?: string[];
  /** Pose ou retire un filtre du catalogue (popover 3b). */
  toggleFilter?(id: string): void;
  /** Retire UN filtre (la croix d'un tag 4a). Repli : `toggleFilter`. */
  removeFilter?(id: string): void;
  /** Retire tous les filtres (l'entree de sortie du popover). */
  clearFilters?(): void;
  /** La pile de tri, ORDONNEE : les tags de tri 4a. */
  sortStack?: SortLevel[];
  /** Un clic du popover de tri : ajoute un niveau en bout, le retire au second clic. */
  toggleSort?(key: string): void;
  /** Inverse le sens d'UN niveau (le corps d'un tag de tri). */
  invertSort?(key: string): void;
  /** Retire UN niveau (la croix d'un tag de tri). */
  removeSort?(key: string): void;
  /** Vide la pile (l'entree de sortie du popover, "Ordre d'origine"). */
  clearSort?(): void;
  /**
   * Vide les filtres ET la pile de tri EN UN SEUL RENDU : le mot "Effacer
   * filtres" de la zone 4a. Ce n'est pas `clearFilters()` puis `clearSort()`,
   * deux setState en cascade feraient clignoter la liste. Rien d'autre ne
   * bouge : ni la recherche, ni la porte, ni l'element ouvert. Absent = le mot
   * n'est pas rendu.
   */
  clearAll?(): void;
  /** Le tiroir (zone 4) est-il deplie. */
  drawerOpen?: boolean;
  /** L'etoile (3b) : ouvre / referme le tiroir a la demande. */
  setDrawerOpen?(open: boolean): void;
  /** Les favoris de la porte active (presets puis favoris gardes), zone 4c. */
  favorites?: ScreenFavorite[];
  /** Le favori qui correspond EXACTEMENT a l'etat courant, allume en 4c. */
  activeFavoriteId?: string | null;
  /** Applique un favori (pose ses filtres et sa pile de tri). */
  applyFavorite?(id: string): void;
  /**
   * Oublie un favori GARDE par l'utilisateur (croix des pastilles `user-*` de
   * la zone 4c, revelee au survol). Les presets de la porte ne se retirent
   * jamais : le chassis ne rend la croix que sur les favoris utilisateur, et
   * le moteur ignore de toute facon un id de preset.
   */
  forgetFavorite?(id: string): void;
  /** Garde l'etat courant en favori (le bouton 4b). */
  keepFavorite?(): void;
  /** Le bouton 4b est-il actif. Faux = desactive, sa raison en title. */
  canKeepFavorite?: boolean;
  /**
   * LE MODE ARCHIVES est-il allume. Ce n'est PAS un filtre de plus : le mode
   * decide SEUL du jeu, il passe AVANT tout le reste. Allume, on voit les
   * fiches archivees et RIEN D'AUTRE, et les filtres ne s'y appliquent pas (une
   * archive est hors du travail courant, la trancher par statut n'aurait pas de
   * sens). C'est pour ca qu'il vit ICI et pas dans `activeFilterIds`.
   */
  archivesMode?: boolean;
  /**
   * Allume / eteint le mode. Absent : l'entree "Archives" du popover se rend
   * quand meme, ETEINTE avec sa raison (G16) : elle est promise dans toutes les
   * apps, un ecran ne la fait pas disparaitre en oubliant de la brancher.
   *
   * ENTRER passe par une FENETRE (le chassis l'ouvre), SORTIR non : entrer
   * change entierement ce que l'ecran montre, sortir est reversible et sans
   * consequence, redemander serait de la ceremonie.
   */
  setArchivesMode?(on: boolean): void;
  /**
   * LES GESTES RARES DE LA FICHE (menu "..." de la rangee 3c) : Archiver /
   * Desarchiver et Supprimer, construits par le moteur depuis les verbes de la
   * porte (`useScreen().recordMenuActions`).
   *
   * ILS PASSENT PAR `tools` ET C'EST LE POINT. La promesse du chantier 2_0 est
   * "dans toutes les apps, sur toutes les portes, sans qu'aucune app n'ecrive
   * une ligne" : tant que ces gestes n'arrivaient que par la prop explicite, un
   * ecran qui passait `tools={screen}` sans y penser gardait un menu "..." vide
   * pendant que l'ecran d'a cote proposait Archiver et Supprimer. Meme menu,
   * meme geste, deux vocabulaires selon l'app. Passe par `tools`, un ecran deja
   * migre les recoit sans rien recabler.
   *
   * LA PROP `recordMenuActions` DE `ScreenLayoutProps` RESTE UNE SURCHARGE :
   * fournie, elle gagne ; absente, c'est ce champ qui sert.
   */
  recordMenuActions?: ScreenAction[];
  /**
   * La porte active : seul `sections` est lu. `false` = cette porte ne se
   * tranche pas : l'etoile, le CATALOGUE de filtres et le tiers des RACCOURCIS
   * du tiroir (4b + 4c, filet compris) sont masques ; le groupe d'outils garde
   * le tri ET l'outil filtre, qui ne porte alors que l'entree Archives.
   * `useScreen().door` s'y passe tel quel.
   *
   * ATTENTION, LA REGLE A CHANGE AU CHANTIER 2_0 : l'outil filtre n'est plus
   * masque sur une porte sans tranches ni sur un catalogue vide. Son catalogue
   * n'est JAMAIS vide, puisque le socle y injecte l'entree Archives ; sur une
   * porte sans tranches, le popover ne montre QUE cette entree. Le tiroir, lui,
   * se rend aussi sur une telle porte tant que le mode est allume, pour porter
   * le tag qui explique la liste.
   */
  door?: { sections: boolean };

  /* ------------------------------------------------------------------ */
  /* LA SELECTION DE LIGNES ET LE LOT (chantier 3_0)                     */
  /*                                                                    */
  /* Tout arrive par `tools={screen}`, comme `recordMenuActions` au      */
  /* chantier 2_0 : le moteur pose ces champs au PREMIER niveau de son   */
  /* etat, et un ecran deja migre recoit la barre de lot sans recabler   */
  /* une seule ligne. Tout est optionnel : un champ absent eteint le     */
  /* geste correspondant, il ne casse pas l'ecran.                       */
  /*                                                                    */
  /* LE CHASSIS NE BOUCLE JAMAIS sur un lot. Il MONTE les fenetres et    */
  /* leur passe les ids ; l'ecriture sequentielle, la progression, le    */
  /* compte rendu et le rejeu des echecs vivent dans `BulkRunner`.       */
  /* ------------------------------------------------------------------ */

  /**
   * Les ids des lignes COCHEES. Non vide = la rangee 3 change de nature et
   * devient la BARRE DE LOT (son troisieme etat, apres collection et fiche).
   *
   * L'invariant du moteur, le plus important de ce chantier : une ligne qui
   * sort de l'ecran sort de la selection. Le chassis ne le tient pas, il en
   * DEPEND : il affiche ce que le moteur lui donne, et le compte ne parle
   * donc jamais de fiches qu'on ne voit plus.
   */
  selection?: string[];
  /**
   * Annule la selection : la croix de la barre, et `Echap`. Absente, la croix
   * se rend ETEINTE avec sa raison (G16) : un etat sans sortie serait une
   * nasse, on ne le cache pas, on le dit.
   */
  clearSelection?(): void;
  /**
   * LA SURFACE QUI PEUT CHOISIR AUJOURD'HUI. `'table'` : le tableau porte les
   * cases. `null` : aucune surface ne peut choisir (la selection n'existe QU'EN
   * TABLEAU, arbitrage du fondateur du 07/08/2026 : agir sur un lot demande de
   * VOIR les valeurs sur lesquelles on agit, et le tableau est la seule vue qui
   * les montre toutes).
   *
   * LE CHASSIS S'EN SERT POUR REFUSER, JAMAIS POUR ALLUMER : voir
   * `bulkActive` dans le corps du composant. Un champ absent ne doit pas
   * pouvoir eteindre la barre en silence.
   */
  selectionLevel?: 'table' | null;
  /**
   * LES CHAMPS MODIFIABLES de la vue active, pour la fenetre "Modification
   * groupee". Vide : la fenetre le DIT, elle ne s'ouvre pas vide.
   */
  bulkEditFields?: BulkEditField[];
  /**
   * LES TROIS PRIMITIVES A DOCUMENT UNIQUE. Le SDK n'expose pas d'ecriture
   * groupee, et chaque document doit passer sa validation Zod et poser son
   * `updatedBy` : le lot s'ecrit donc UNE FICHE A LA FOIS. C'est `BulkRunner`
   * qui boucle, jamais le chassis et jamais le moteur.
   */
  archiveOne?(id: string): Promise<void>;
  removeOne?(id: string): Promise<void>;
  writeCell?(id: string, fieldKey: string, value: unknown): Promise<void>;
  /**
   * CE QUE LA PORTE SAIT FAIRE, pour GRISER AVEC SA RAISON (G16), jamais
   * cacher. `null` = le geste est disponible ; une chaine = la RAISON, ecrite
   * pour un utilisateur et pas pour un developpeur (lecon du chantier 2_0 :
   * jamais "l'app doit declarer remove()").
   *
   * Champ absent : les trois gestes sont consideres DISPONIBLES. Le defaut
   * inverse eteindrait toute la barre au premier ecran qui oublie de le
   * remplir, sans qu'aucun build ne rougisse.
   */
  bulkAvailability?: { archive: string | null; remove: string | null; edit: string | null };
  /**
   * LES LIGNES PROJETEES DE LA VUE, lues pour UNE seule chose : donner un NOM
   * a chaque fiche du lot ("Elles NOMMENT les fiches tombees"). `title` d'une
   * `ProjectedRow` n'est jamais vide, il retombe sur l'id.
   *
   * LE NOM EST LU AU CLIC ET GELE, jamais relu apres l'ecriture : les fiches
   * ecrites ont deja quitte la vue a ce moment-la, et le compte rendu
   * afficherait des identifiants exactement pour les lignes qu'il faut pouvoir
   * rattraper.
   *
   * `useScreen().rows` s'y passe tel quel : le nom existait deja, aucune
   * declaration nouvelle a ecrire cote moteur.
   */
  rows?: ProjectedRow[];
}

export interface ScreenLayoutProps {
  /* ---- Zone 1 : rail (identite + menu). S'arrete au menu. ---- */
  /** Nom de l'app affiche en tete du rail. */
  appName?: string;
  /**
   * Icone de l'app, rendue en tete du rail (zone 1a), a gauche du nom.
   * UNE par app, identique sur tous les ecrans, jamais remplacee (G1).
   * Icone @taki/ui, size 18 conseille.
   */
  appIcon?: ReactNode;
  /** Entrees du menu (les portes). Zone rendue seulement si non vide. */
  menuItems?: ScreenMenuItem[];
  /** Id de l'entree active (aplat surface-3, encre pleine). */
  activeMenuId?: string;
  /** Callback quand une entree du menu est cliquee. */
  onMenuSelect?: (id: string) => void;

  /* ---- Zone 2a : titre (OBLIGATOIRE) ---- */
  /**
   * Titre de repli de l'ecran : le h1 affiche le label de l'entree de menu
   * active (`activeMenuId`) quand elle existe, sinon cette prop.
   */
  title: string;

  /* ---- Zone 2b : la recherche, centree sur l'ecran ---- */
  /**
   * Callback de saisie : sa presence AFFICHE le champ (h 2rem, icone
   * etincelle), au centre de l'en-tete. L'app filtre elle-meme son contenu
   * avec la valeur recue (`useScreen().setSearch`).
   */
  onSearchChange?: (value: string) => void;
  /**
   * Valeur controlee du champ. Optionnel : sans lui, le gabarit gere la saisie
   * en interne (le champ reste fonctionnel), tout en remontant chaque frappe a
   * `onSearchChange`. Le fournir pour un controle total (reset, valeur externe).
   */
  searchValue?: string;
  /**
   * Placeholder du champ, celui de la PORTE active pour un ecran v2
   * (`useScreen().door.searchPlaceholder`). Defaut : `DEFAULT_SEARCH_PLACEHOLDER`.
   */
  searchPlaceholder?: string;

  /* ---- Zone 2c : stats ---- */
  /**
   * Les stats de la porte active, alignees a droite. PLAFOND DE 3, tenu ici :
   * au-dela, le surplus n'est pas rendu. Sans stat, l'en-tete garde sa grille
   * (l'equivalent React du `[hidden]` du proto) : le titre ne bouge pas.
   */
  headerStats?: ScreenStat[];

  /* ---- Zone 3a : les actions de la porte ---- */
  /**
   * Les actions de la PORTE ACTIVE (`useScreen().door.actions` pour un ecran
   * v2), rendues en rangee au-dessus du contenu, 3 par porte maximum. La regle
   * "+ Ajouter" et la regle du lime unique sont tenues ici (voir
   * `ScreenAction.kind`). Le nom `quickActions` est garde tel quel pour que
   * les ecrans V1 compilent sans modification : memes declarations, nouvel
   * emplacement (le rail n'a plus d'actions).
   */
  quickActions?: ScreenAction[];
  /**
   * Les actions de la VUE active (`useScreen().viewActions`), rendues APRES
   * les actions de l'ecran, TOUJOURS en secondaire, quoi qu'elles declarent :
   * le lime unique appartient a l'ecran, jamais a une vue. C'est la seule
   * porte d'une vue vers la rangee 3a.
   */
  viewActions?: ScreenAction[];

  /* ---- Zone 3b + 4 : outils, tags, favoris (gabarit v2) ---- */
  /**
   * L'etat du moteur, passe tel quel : `tools={screen}`. Ce qui l'allume :
   * le groupe d'outils (filtre, tri, etoile) en 3b et le tiroir (tags 4a,
   * garder 4b, favoris 4c) en zone 4. Absent : ni outils ni tiroir, l'ecran
   * V1 garde exactement sa rangee d'avant.
   */
  tools?: ScreenToolset;
  /**
   * Le CATALOGUE de tri de l'ecran (les cles proposees par le popover de
   * l'outil tri). C'est une declaration d'ecran, pas un etat : le moteur ne le
   * connait pas. Sans lui, l'outil tri ne se rend pas. Ignore sans `tools`.
   */
  sortOptions?: ScreenSortOption[];
  /**
   * LECTURE V1 : les pastilles de filtre a choix unique, rendues dans la
   * rangee 3 (l'ancienne zone 4). Remplacees par `tools` des qu'un ecran
   * migre : un ecran ne passe jamais les deux.
   */
  filters?: ScreenFilter[];
  /** Id de la pastille active (V1, encre pleine). */
  activeFilterId?: string;
  /** Callback quand une pastille est cliquee (V1). */
  onFilterSelect?: (id: string) => void;

  /* ---- Zone 3c : le selecteur de vues, epingle, JAMAIS conditionnel ---- */
  /**
   * Les vues proposees par l'ecran (`useScreen().views`), rendues en segmented
   * control epingle a droite de la rangee 3 : une entree non satisfaite est
   * GRISEE avec sa raison au survol, jamais absente. Une vue n'est JAMAIS une
   * entree de `menuItems` : le menu designe un JEU de donnees, le selecteur
   * choisit le RENDU. Sans cette prop, la rangee 3 est celle d'un ecran sans
   * vues (aucun ecran existant ne bouge).
   *
   * PASSER LA LISTE COMPLETE, entrees `record` comprises : c'est elle qui
   * DEDUIT le niveau. Le selecteur, lui, n'affiche que le niveau `collection`
   * (le filtrage est fait ici, au rendu, jamais a la source).
   */
  views?: ViewOption[];
  /** Id de la vue active. */
  activeViewId?: ViewId;
  /** Callback quand une vue est choisie (`useScreen().setView`). */
  onViewSelect?: (id: ViewId) => void;

  /* ---- Rangee 3, partie gauche au niveau `record` ---- */
  /*
   * Ces quatre props ne se rendent QUE si la vue active est de niveau `record`
   * (niveau deduit de `views` + `activeViewId`, jamais demande). Elles sont
   * donc inertes sur un ecran sans vues, meme si une app les passe par erreur,
   * et elles n'entrent JAMAIS dans le calcul de rendu de la rangee 3 :
   * l'indicateur d'enregistrement VIT DANS la rangee, il n'existe que la ou
   * elle existe.
   */
  /**
   * Ferme la FICHE et ramene a la derniere vue collection
   * (`useScreen().openRecord(null)`). Absente : pas de puce, le selecteur
   * reste la seule sortie.
   *
   * Ce n'est PAS la sortie de l'app : celle-la vient du shell par
   * `ScreenNav.onBack` et vit sur la puce d'icone de la zone 1a.
   */
  onRecordExit?: () => void;
  /**
   * Le libelle de la puce de sortie : le nom de la vue vers laquelle on
   * revient ("Tableau", "Liste"). Cote ecran :
   * `screen.views.find((v) => v.id === screen.lastCollectionViewId)?.label`.
   * Defaut "Retour" : une puce muette n'existe pas.
   */
  recordExitLabel?: string;
  /** L'etat d'enregistrement de l'ecran (`useScreen().saveState`). */
  saveState?: SaveState;
  /** Rejoue la derniere ecriture echouee (`useScreen().retrySave`). */
  onRetrySave?: () => void;

  /* ---- Rangee 3, groupe EPINGLE au niveau `record` (3c) ---- */
  /*
   * Au niveau `record`, le selecteur de vues n'est pas rendu : on ne change
   * pas de vue depuis une fiche, on en SORT d'abord. Sa place est prise par le
   * CURSEUR de fiche, un filet, puis les gestes SUR la fiche. Ces deux props
   * se passent SANS condition, comme les quatre relais ci-dessus : le gabarit
   * ne les lit qu'au niveau `record`.
   */
  /**
   * LE CURSEUR DE FICHE (`useScreen().cursor`), gele a l'ouverture : de quel
   * element on vient, ou l'on est ("9 / 30"), ou l'on va. Il vivait dans la
   * bande d'identite de `core.fiche` (zone 5) : il est remonte dans la rangee
   * 3, la ou vivent deja la sortie de fiche et l'indicateur d'enregistrement.
   * Absent : aucun groupe epingle au niveau fiche (une fiche sans curseur reste
   * lisible, elle garde sa puce de sortie).
   */
  recordCursor?: Cursor;
  /**
   * Ouvre un AUTRE element (`useScreen().openRecord`) : ce que font les deux
   * fleches du curseur. Naviguer, c'est ouvrir un autre id, jamais bouger la
   * vue. Absent : les fleches sont rendues DESACTIVEES, jamais muettes.
   */
  onRecordNavigate?: (id: string) => void;
  /**
   * LES GESTES RARES SUR LA FICHE : le contenu du menu "..." de la rangee 3c
   * (`useScreen().recordMenuActions`). Archiver, Desarchiver, Supprimer. Ces
   * gestes sont RARES, un menu est leur place ; la rangee 3a porte ce qu'on
   * fait souvent.
   *
   * SURCHARGE, PAS CHEMIN PRINCIPAL : le chemin normal est `tools`
   * (`ScreenToolset.recordMenuActions`), qui les apporte a tout ecran deja
   * migre sans une ligne de cablage. Cette prop-ci ne sert qu'a un ecran qui
   * veut fournir SES gestes a la place de ceux du moteur ; fournie, elle gagne.
   *
   * Le nom dit ou ils ATTERRISSENT (le menu "..." de la 3c) et non ce qu'ils
   * sont : a cote de `quickActions` et `viewActions`, qui vivent tous deux dans
   * la rangee 3a, un troisieme `*Actions` sans autre precision se lirait comme
   * un troisieme groupe de boutons de cette rangee.
   *
   * Une action `disabled` reste VISIBLE avec sa raison en `title` (G16, G18) :
   * un bouton qui apparait et disparait selon l'app fabrique exactement le
   * "pourquoi je ne peux pas supprimer ici" qu'on veut eviter. "Supprimer" y
   * reste SECONDAIRE : aucun rouge dans le menu, le rouge ne vit que dans la
   * fenetre de confirmation. Une action qui porte un `confirm` voit sa fenetre
   * ouverte par le CHASSIS.
   *
   * Vide ou absente des deux cotes : le menu s'ouvre quand meme et DIT pourquoi
   * il est vide.
   */
  recordMenuActions?: ScreenAction[];

  /* ---- Zone 5 : la ref du conteneur qui defile ---- */
  /**
   * Ref posee sur la zone 5 (le seul conteneur de contenu qui defile). Le
   * gabarit la PRETE : le `ViewHost` s'en sert pour la memoire de defilement,
   * et les vues la recoivent dans `ViewProps.scrollRef` (une vue en
   * `scroll: 'self'` en a besoin pour se mesurer). A brancher sur
   * `useScreen().scrollRef`.
   */
  scrollRef?: Ref<HTMLElement>;

  /* ---- Zone 5 : contenu + etats standard ---- */
  /**
   * Etat chargement : remplace le contenu par un skeleton. Temporisation
   * integree (useLoadingState) : le skeleton n'apparait qu'apres un court
   * delai (donnee rapide = aucun flash), reste affiche une duree minimale
   * (pas de clignotement), et un chargement trop long (10 s) bascule vers
   * l'etat erreur standard (avec bouton reessayer si `onRetry`).
   */
  loading?: boolean;
  /**
   * @deprecated Ignore : l'etat chargement est un skeleton (prop `skeleton`),
   * plus un bloc de texte. Conserve pour ne pas casser les ecrans existants.
   */
  loadingText?: string;
  /**
   * Skeleton de zone 5 sur mesure, compose depuis les primitives Skeleton*
   * de @taki/ui, fidele au contenu reel (zero saut de mise en page).
   * Defaut : <SkeletonListRows /> (le pattern de liste de reference).
   */
  skeleton?: ReactNode;
  /** Message d'erreur : remplace le contenu (prioritaire sur tout). */
  error?: string | null;
  /**
   * Titre de l'etat erreur. Defaut "Something went wrong" ; pour l'erreur de
   * chargement trop long, defaut "Still loading, something may be wrong".
   */
  errorTitle?: string;
  /** Si fourni, l'etat erreur affiche un bouton reessayer. */
  onRetry?: () => void;
  /** Libelle du bouton reessayer. Defaut "Retry". */
  retryLabel?: string;
  /** Etat vide : remplace le contenu (apres error et loading). */
  empty?: boolean;
  /** Titre de l'etat vide. Defaut "Nothing here yet". */
  emptyTitle?: string;
  /** Phrase d'aide sous le titre de l'etat vide (ex. quoi faire pour demarrer). */
  emptyHint?: string;
  /** Contenu de l'ecran (zone 5). Ignore quand un etat standard s'affiche. */
  children?: ReactNode;
}

/**
 * Entree du menu (zone 1b). Actif = aplat --surface-3 + encre --ink-900 +
 * aria-current ; le compteur reste en mono --ink-400, gele par doctrine.
 * L'aplat d'accent (tint lime) n'entre plus ici : la teinte appartient au
 * rail du DOCK et aux tags, jamais a deux menus a la fois.
 */
function RailItem({
  icon,
  label,
  count,
  active = false,
  onClick,
}: {
  icon?: ReactNode;
  label: string;
  count?: number | string;
  active?: boolean;
  onClick?: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'true' : undefined}
      className={cn(
        'flex h-9 w-full shrink-0 items-center gap-2.5 rounded-[0.625rem] px-2.5 text-left font-sans text-sm font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
        active ? 'bg-surface-3 font-semibold text-ink-900' : 'text-ink-700 hover:bg-surface-1',
      )}
    >
      {icon != null && (
        <span className="flex shrink-0 items-center justify-center">{icon}</span>
      )}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {count != null && (
        <span className="shrink-0 font-mono text-[0.6875rem] text-ink-400">{count}</span>
      )}
    </button>
  );
}

/**
 * L'INDICATEUR D'ENREGISTREMENT de la rangee 3 (niveau `record` uniquement).
 *
 * Quatre etats, et le premier ne rend RIEN : avant la premiere ecriture, la
 * rangee ne porte aucun encombrement. Le rond tournant est ici legitime au
 * regard de la doctrine des etats de chargement (tokens.css) : une ecriture est
 * une ACTION PONCTUELLE, pas un chargement de contenu (qui, lui, prend un
 * squelette). L'echec, lui, n'est pas qu'un texte : il porte sa relance, parce
 * qu'une saisie perdue sans explication est le pire des deux.
 */
function SaveIndicator({
  state,
  onRetry,
}: {
  state: SaveState;
  onRetry?: () => void;
}): JSX.Element | null {
  if (state.mode === 'idle') return null;

  if (state.mode === 'saving') {
    return (
      <span role="status" className="flex h-8 shrink-0 items-center gap-2 text-xs text-ink-400">
        <span className="taki-spinner" />
        Enregistrement...
      </span>
    );
  }

  if (state.mode === 'saved') {
    // "jamais un trou, jamais undefined a l'ecran" : sans heure, on dit juste
    // que c'est enregistre.
    const time =
      state.at != null
        ? state.at.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
        : null;
    return (
      <span role="status" className="flex h-8 shrink-0 items-center gap-1.5 text-xs text-ink-400">
        <IconCheck size={13} />
        {time != null ? `Enregistre a ${time}` : 'Enregistre'}
      </span>
    );
  }

  return (
    <>
      <span
        role="alert"
        className="flex h-8 shrink-0 items-center rounded-pill border border-danger-border bg-danger-tint px-3.5 font-sans text-xs font-semibold text-danger"
      >
        Echec de l'enregistrement
      </span>
      {onRetry != null && (
        <Button variant="secondary" size="sm" className="shrink-0" onClick={onRetry}>
          Reessayer
        </Button>
      )}
    </>
  );
}

/**
 * Bloc d'etat standard centre dans la zone 5 (chargement, vide, erreur).
 *
 * Exporte pour le seul `ViewHost` (interne au socle), qui rend les MEMES etats
 * quand la zone 5 est pilotee par une vue : deux implementations divergeraient
 * au premier ajustement. Volontairement absent du barrel `@taki/ui` : une app
 * ne rend jamais un etat standard elle-meme, elle passe les props du gabarit.
 */
export function StateBlock({
  title,
  hint,
  action,
  role,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
  /** `alert` pour l'erreur, `status` pour le chargement : annonce aux lecteurs d'ecran. */
  role?: 'alert' | 'status';
}): JSX.Element {
  return (
    <div
      role={role}
      className="flex h-full flex-col items-center justify-center gap-1.5 px-6 text-center"
    >
      <p className="text-[0.9375rem] font-semibold text-ink-900">{title}</p>
      {hint != null && <p className="max-w-sm text-[0.8125rem] text-ink-500">{hint}</p>}
      {action != null && <div className="mt-2">{action}</div>}
    </div>
  );
}

/**
 * Un bouton du groupe d'outils (3b) : meme boite que les entrees du selecteur
 * de vues (h 1.75rem sur 2rem, actif encre + ombre de carte). Un outil dont le
 * reglage n'est pas au repos porte un POINT (0.3125rem) : un filtre replie et
 * muet est un filtre qu'on oublie.
 *
 * CE POINT EST EN LIME (`--accent-dot`), demande du fondateur du 07/08/2026, et
 * ce n'est PAS une entorse a G17 : `--accent-dot` est le jeton des PETITS
 * MARQUEURS, pas le lime du bouton d'ajout ; a 5px l'encre se colle au glyphe
 * qu'elle surmonte et les deux formes noires se lisent comme une bavure ; et
 * surtout un point n'est pas un etat ACTIF au sens de la regle, aucun geste ne
 * l'atteint, il ne se clique pas. Il RAPPELLE qu'un reglage est pose ailleurs,
 * dans le popover replie.
 *
 * A NE PAS "REPARER" : le point qui marque une entree ACTIVE a l'interieur d'un
 * popover (`ToolPopover.tsx`) reste a l'ENCRE et ne s'aligne pas sur celui-ci.
 * Celui-la designe un etat qu'on CHOISIT, exactement ce que G17 vise. Les deux
 * points ne disent pas la meme chose : l'un rappelle, l'autre designe.
 */
function ToolButton({
  icon,
  label,
  marked = false,
  expanded,
  controls,
  haspopup = false,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  marked?: boolean;
  expanded: boolean;
  controls?: string;
  haspopup?: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-expanded={expanded}
      {...(haspopup ? { 'aria-haspopup': 'listbox' as const } : {})}
      {...(controls != null ? { 'aria-controls': controls } : {})}
      onClick={onClick}
      className={cn(
        'relative flex h-7 w-8 shrink-0 cursor-pointer items-center justify-center rounded-sm transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
        expanded ? 'bg-surface text-ink-900 shadow-card' : 'text-ink-500 hover:text-ink-900',
      )}
    >
      {icon}
      {marked && (
        <span
          aria-hidden
          className="absolute right-0.5 top-0.5 h-[0.3125rem] w-[0.3125rem] rounded-pill bg-accent-dot"
        />
      )}
    </button>
  );
}

/**
 * LA RAISON DU MODE, ECRITE UNE SEULE FOIS.
 *
 * Elle sort a QUATRE endroits du chassis, et c'est voulu : sous le catalogue
 * grise du popover, en `title` des tags de FILTRE eteints (4a), en `title` des
 * pastilles de favori eteintes (4c), et dans la fenetre d'entree du mode. Quatre
 * copies d'une meme phrase divergeraient au premier ajustement, et l'utilisateur
 * lirait alors deux regles la ou il n'y en a qu'une.
 *
 * ELLE NE PARLE QUE DES FILTRES : le TRI, lui, reste disponible et s'applique
 * pour de vrai dans les archives (le moteur ne neutralise que les filtres).
 * Etendre la phrase au tri dissuaderait d'un geste qui marche.
 */
const ARCHIVES_FILTER_OFF = "Les filtres ne s'appliquent pas aux archives.";
/** La meme regle, dans une fenetre qui vient de nommer les archives. */
const ARCHIVES_FILTER_OFF_SHORT = "Les filtres ne s'y appliquent pas.";

/**
 * LE POPOVER DE L'OUTIL FILTRE (3b), ET L'ENTREE "ARCHIVES" QUE LE SOCLE Y
 * INJECTE.
 *
 * POURQUOI IL NE REUTILISE PAS `ToolPopover`. Le popover de TRI, lui, le
 * reutilise et continuera : c'est un catalogue et une entree de sortie, rien de
 * plus. Celui du filtre porte desormais trois choses que le tri n'aura jamais :
 * une entree INJECTEE PAR LE SOCLE sous un filet (aucune app ne la declare),
 * le GRISAGE INERTE de tout le catalogue quand le mode est allume, et la RAISON
 * de ce grisage ecrite dessous. `ToolPopover` reste la primitive commune des
 * panneaux d'outil ; ce fichier compose la sienne au-dessus du meme vocabulaire
 * (--shadow-pop, --radius-md, items de 2rem). Les deux panneaux doivent rester
 * jumeaux : toute retouche de l'un se reporte sur l'autre.
 *
 * L'ENTREE EST TOUJOURS LA, dans toutes les apps et sur toutes les portes, meme
 * quand la porte n'a aucun filtre a proposer : c'est le sens de "obligatoire"
 * dans la demande du fondateur. Ce n'est pas une option que l'app active, c'est
 * le socle qui l'impose. Sur une porte sans tranches, le popover ne montre donc
 * QUE cette entree, et ni filet ni entree de sortie (un filet sans rien
 * au-dessus est un trait perdu, une sortie sans catalogue ne vide rien).
 *
 * PAS D'INTERRUPTEUR, et c'est le point. Deux formes avaient ete posees cote a
 * cote dans le prototype ; l'interrupteur a ete retire pour une raison qui
 * n'etait pas visible au depart : un interrupteur promet une bascule IMMEDIATE,
 * or entrer dans le mode ouvre une fenetre qui l'explique. Le controle mentait
 * sur ce qui allait se passer.
 *
 * ALLUMEE, elle devient un APLAT ENCRE et gagne une croix de sortie ; les
 * autres entrees se grisent et deviennent inertes, avec leur raison ecrite
 * dessous. Le menu MONTRE ce qui ne s'applique plus au lieu de le dire.
 */
function FilterPopover({
  id,
  items,
  onPick,
  showCatalogue,
  clearDisabled,
  onClear,
  archivesMode,
  onArchivesToggle,
}: {
  id: string;
  items: ToolPopoverItem[];
  onPick: (id: string) => void;
  /** Le catalogue de la porte se rend-il. Faux : seule l'entree Archives reste. */
  showCatalogue: boolean;
  clearDisabled: boolean;
  onClear: () => void;
  archivesMode: boolean;
  /** Absent : l'entree se rend ETEINTE avec sa raison, jamais absente (G16). */
  onArchivesToggle?: (on: boolean) => void;
}): JSX.Element {
  /*
   * LES AUTRES ENTREES S'ETEIGNENT PAR `aria-disabled` + `pointer-events-none`,
   * jamais par `disabled` : elles doivent rester ANNONCEES (c'est tout l'objet
   * de la demonstration, montrer ce qui ne s'applique plus) alors qu'un
   * `disabled` les sortirait de l'ordre de tabulation ET de l'arbre
   * d'accessibilite. `tabIndex -1` les retire du parcours clavier sans les
   * effacer, `pointer-events-none` les retire de la souris, et le `onClick`
   * garde sa propre garde : trois verrous pour un seul etat, parce qu'un menu
   * qui repond alors qu'il dit ne pas s'appliquer est pire que pas de menu.
   */
  const inert = archivesMode;
  const archivesLabel =
    onArchivesToggle == null
      ? "Archives : le mode n'est pas branche sur cet ecran"
      : archivesMode
        ? 'Archives : quitter le mode'
        : 'Archives : voir les fiches archivees';

  return (
    <div
      id={id}
      role="listbox"
      aria-label="Filtrer"
      className={cn(
        'absolute right-0 top-[calc(100%+0.4375rem)] z-20 flex min-w-48 flex-col p-1',
        'rounded-md border border-border bg-surface shadow-pop',
      )}
    >
      {showCatalogue && (
        <>
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              role="option"
              aria-selected={item.active}
              aria-disabled={inert || undefined}
              tabIndex={inert ? -1 : undefined}
              onClick={inert ? undefined : () => onPick(item.id)}
              className={cn(
                'flex h-8 w-full items-center gap-2 rounded-sm px-2 text-left',
                'font-sans text-[0.8125rem] font-medium text-ink-700 transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
                item.active && 'font-semibold text-ink-900',
                inert
                  ? 'pointer-events-none cursor-default opacity-35'
                  : 'cursor-pointer hover:bg-surface-1 hover:text-ink-900',
              )}
            >
              {/* Le point d'encre : toujours rendu, visible quand actif. Sa
                  boite reserve la place, les libelles restent alignes actif ou
                  pas. Il reste a l'ENCRE : celui-la designe un etat qu'on
                  CHOISIT (G17), contrairement au point de l'outil, qui rappelle. */}
              <span
                aria-hidden
                className={cn(
                  'h-1.5 w-1.5 shrink-0 rounded-pill bg-ink-900',
                  item.active ? 'opacity-100' : 'opacity-0',
                )}
              />
              <span className="min-w-0 flex-1 truncate">{item.label}</span>
              {item.detail != null && (
                <span className="shrink-0 pl-3 font-mono text-[0.6875rem] font-medium text-ink-400">
                  {item.detail}
                </span>
              )}
            </button>
          ))}
          {/* L'ENTREE DE SORTIE du catalogue, sous son filet : revenir a l'etat
              de base sans retirer les choix un par un. Elle se grise avec le
              reste en mode archives : elle n'a rien a vider qui s'y applique. */}
          <button
            type="button"
            aria-disabled={inert || clearDisabled || undefined}
            tabIndex={inert ? -1 : undefined}
            onClick={inert || clearDisabled ? undefined : onClear}
            className={cn(
              'mt-1 h-8 w-full border-t border-border-soft bg-transparent pl-2 pt-[0.4375rem] text-left',
              'font-sans text-[0.8125rem] font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
              clearDisabled ? 'cursor-default text-ink-300' : 'cursor-pointer text-ink-500 hover:text-ink-900',
              inert && 'pointer-events-none cursor-default opacity-35',
            )}
          >
            Tout afficher
          </button>
          {/* LE FILET QUI ANNONCE L'ENTREE DU SOCLE. Il ne se rend qu'avec le
              catalogue : sur une porte sans tranches il n'y a rien a separer. */}
          <div aria-hidden className="my-1 h-px shrink-0 bg-border-soft" />
        </>
      )}
      {/* L'ENTREE "ARCHIVES", injectee par le SOCLE, tout en bas. Ligne a icone
          PLEINE LARGEUR (h 2.25rem, un cran au-dessus des entrees de catalogue :
          ce n'est pas une entree de plus, c'est une porte de sortie du jeu). */}
      <button
        type="button"
        aria-pressed={archivesMode}
        aria-disabled={onArchivesToggle == null || undefined}
        title={archivesLabel}
        aria-label={archivesLabel}
        onClick={onArchivesToggle == null ? undefined : () => onArchivesToggle(!archivesMode)}
        className={cn(
          'flex h-9 w-full shrink-0 items-center gap-2 rounded-sm px-2 text-left',
          'font-sans text-[0.8125rem] font-medium transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
          onArchivesToggle == null
            ? 'cursor-default text-ink-300'
            : archivesMode
              ? 'cursor-pointer bg-ink-900 font-semibold text-surface'
              : 'cursor-pointer text-ink-700 hover:bg-surface-1',
        )}
      >
        {/* La caisse d'archives fermee du set du fondateur (`archive.svg`). */}
        <span aria-hidden className="flex shrink-0">
          <IconArchive size={14} />
        </span>
        <span className="min-w-0 flex-1 truncate">Archives</span>
        {archivesMode && (
          /* LA CROIX DE SORTIE. Elle ne double pas le geste du bouton, elle le
             NOMME : sur un aplat encre, rien ne dit qu'un second clic ressort.
             Elle est decorative, c'est le bouton entier qui bascule. */
          <span aria-hidden className="flex shrink-0">
            <IconClose size={10} />
          </span>
        )}
      </button>
      {archivesMode && (
        /* LA RAISON, sous le menu grise. En --ink-500 et NON --ink-400 : le
           systeme reserve --ink-300 et --ink-400 aux libelles eteints et aux
           filets, jamais a une information A LIRE. Or cette phrase est la charge
           utile de tout l'etat : elle enseigne une regle qu'on ne devinerait pas
           seul. */
        <p className="px-2 pb-0.5 pt-1.5 text-xs leading-[1.4] text-ink-500 [text-wrap:pretty]">
          {ARCHIVES_FILTER_OFF}
        </p>
      )}
    </div>
  );
}

/**
 * UN TAG de la zone 4a : l'etat courant rendu en objets retirables. Contour
 * --accent-border sur fond blanc, JAMAIS de remplissage lime : le remplissage
 * pale appartient aux pieces du tag (survol, rang), le lime plein au seul
 * bouton d'ajout. Anatomie : corps (kind + valeur [+ rang] [+ sens]) puis la
 * croix. Le corps d'un tag de TRI inverse son sens ; celui d'un tag de FILTRE
 * est inerte. Encres : valeur --ink-900, kind / rang / sens --ink-600 (jamais
 * de *-soft-ink en texte sous 14px).
 */
function DrawerTag({
  kind,
  value,
  rank,
  dir,
  tone = 'accent',
  off = false,
  offReason,
  onInvert,
  onRemove,
  removeLabel,
}: {
  /** Le genre, en petit : "Filtre", "Tri" ou "Mode" (sans lui, Camera et Nom se lisent pareil). */
  kind: string;
  value: string;
  /** Le niveau de tri, rendu seulement quand la pile en a 2 ou plus. */
  rank?: number;
  /** Sens du tri : chevron du set, pivote pour l'ascendant (jamais un second glyphe). */
  dir?: 'asc' | 'desc';
  /**
   * LE CONTOUR. `accent` (defaut) pour un filtre ou un tri : une TRANCHE de la
   * collection. `mode` pour le tag de MODE : meme boite, meme croix, meme
   * retrait, mais contour `--mode-border` (le seul orange du systeme), parce
   * qu'un mode ne tranche pas la collection, il la change ENTIEREMENT, et
   * aucune autre couleur ne disait ca. Le jeton se lit par son NOM, jamais en
   * dur : il n'a volontairement pas d'utilitaire Tailwind (tokens.css).
   */
  tone?: 'accent' | 'mode';
  /**
   * LE TAG EST ETEINT : il decrit un reglage qui NE S'APPLIQUE PLUS, et il ne
   * repond plus, croix comprise. C'est le cas des tags de FILTRE en mode
   * archives : le mode a deja decide du jeu, et le tiroir affirmerait le
   * contraire du popover (qui grise ses entrees) s'il les laissait pleins,
   * actifs et retirables a deux centimetres de la.
   *
   * MEME TRAITEMENT QUE LES ENTREES INERTES DU POPOVER, et pour les memes
   * raisons : `aria-disabled` et `pointer-events-none` plutot que `disabled`,
   * pour que le tag reste ANNONCE (c'est tout l'objet de la demonstration) sans
   * repondre ni au doigt ni au clavier. Les pieces internes perdent la souris,
   * la coque la garde : c'est elle qui porte la raison en `title`.
   *
   * LE TAG DE TRI NE PREND JAMAIS CET ETAT : le tri, lui, continue de
   * s'appliquer dans les archives.
   */
  off?: boolean;
  /** La raison de l'extinction (G16), rendue en `title` sur la coque. */
  offReason?: string;
  /** Present = le corps est cliquable et inverse le sens. */
  onInvert?: () => void;
  /** Present = la croix se rend et retire le tag. */
  onRemove?: () => void;
  /** L'aria-label ET le title de la croix. Defaut : "Retirer <valeur>". */
  removeLabel?: string;
}): JSX.Element {
  const interactive = onInvert != null && !off;
  const removeText = removeLabel ?? `Retirer ${value}`;
  return (
    <span
      className={cn(
        'flex h-8 shrink-0 items-stretch overflow-hidden rounded-pill border bg-surface text-ink-900',
        tone === 'accent' && 'border-accent-border',
        // Eteint : la MEME opacite que les entrees inertes du popover. La coque
        // garde la souris pour que sa raison reste survolable (G16).
        off && 'opacity-35',
      )}
      {...(off && offReason != null ? { title: offReason } : {})}
      {...(tone === 'mode' ? { style: { borderColor: 'var(--mode-border)' } } : {})}
    >
      <button
        type="button"
        disabled={!interactive}
        aria-disabled={off || undefined}
        {...(interactive ? { onClick: onInvert, title: 'Inverser le sens' } : {})}
        className={cn(
          'flex items-center gap-1.5 border-none bg-transparent pl-3 pr-1.5 font-sans text-xs font-semibold text-inherit transition-colors',
          'focus-visible:outline-none',
          interactive
            ? 'cursor-pointer hover:bg-accent-tint focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-border'
            : 'cursor-default',
          off && 'pointer-events-none',
        )}
      >
        <span className="text-[0.6875rem] font-medium text-ink-600">{kind}</span>
        <span className="whitespace-nowrap">{value}</span>
        {rank != null && (
          <span className="flex h-4 min-w-4 items-center justify-center rounded-xs bg-accent-tint px-[0.1875rem] font-mono text-[0.625rem] font-medium text-ink-600">
            {rank}
          </span>
        )}
        {dir != null && (
          <span aria-hidden className="flex text-ink-600">
            <IconChevronDown size={11} className={cn(dir === 'asc' && 'rotate-180')} />
          </span>
        )}
      </button>
      {onRemove != null && (
        /* ETEINTE, LA CROIX NE RETIRE PLUS RIEN, et c'est la meme regle que le
           popover : retirer un filtre qui ne s'applique pas repondrait a un
           geste qui n'a aucun effet visible sur la liste. Elle garde son
           `aria-label` (le tag reste annonce) mais lache son `title`, sinon
           l'infobulle de la piece masquerait la RAISON portee par la coque. */
        <button
          type="button"
          aria-disabled={off || undefined}
          tabIndex={off ? -1 : undefined}
          onClick={off ? undefined : onRemove}
          {...(off ? {} : { title: removeLabel ?? 'Retirer' })}
          aria-label={removeText}
          className={cn(
            'flex w-6 shrink-0 cursor-pointer items-center justify-center border-none bg-transparent text-ink-400 transition-colors',
            'hover:bg-accent-tint hover:text-accent-ink',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-border',
            off && 'pointer-events-none cursor-default',
          )}
        >
          <IconClose size={10} />
        </button>
      )}
    </span>
  );
}

/** La boite commune des quatre glyphes du groupe de fiche : carre de 2rem. */
const RECORD_NAV_BUTTON =
  'flex h-8 w-8 shrink-0 items-center justify-center rounded-sm border-none bg-transparent text-ink-500 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border';

/**
 * LE GROUPE EPINGLE AU NIVEAU `record` (3c) : le curseur de fiche, un filet,
 * puis les gestes SUR la fiche (partager, menu).
 *
 * IL PREND LA PLACE DU SELECTEUR DE VUES, qui n'est pas rendu sur une fiche :
 * on ne change pas de vue depuis une fiche, on en SORT d'abord. Meme bout de
 * rangee, meme hauteur (2rem), meme bord droit : le groupe epingle ne bouge
 * pas d'un niveau a l'autre, seul son CONTENU change. C'est ce qui fait que la
 * rangee ne saute pas quand on ouvre une ligne.
 *
 * Le groupe est NU (ni contour ni fond, survol seul), la forme retenue par le
 * prototype valide apres cinq essais : le rang se lit, les glyphes se
 * survolent, et la rangee 3 ne gagne aucun objet de plus.
 *
 * LE RANG EST ENTRE LES DEUX FLECHES : il est ce qu'elles font bouger, il ne
 * peut pas etre pose a cote d'elles. Ce N'EST PAS un bouton, il se lit ; sa
 * largeur est RESERVEE (voir `digits` plus bas), sinon passer de 9 a 10 ferait
 * sauter les fleches autour. `font-mono` et `tabular-nums` n'y suffisent PAS :
 * ils egalisent l'avance d'un CHIFFRE, ils ne fixent pas leur NOMBRE.
 *
 * LE FILET separe le DEPLACEMENT dans le jeu (aller a une autre fiche) des
 * gestes SUR la fiche (la partager, l'ouvrir au menu) : sans lui, quatre
 * glyphes de suite se lisent comme une seule famille.
 */
function RecordNavGroup({
  cursor,
  onNavigate,
  actions,
  onRunAction,
}: {
  cursor: Cursor;
  /** Absent = les deux fleches sont desactivees (elles n'ont nulle part ou aller). */
  onNavigate?: (id: string) => void;
  /** Les gestes rares du menu "..." (`recordMenuActions`). Vide : le menu dit pourquoi. */
  actions: ScreenAction[];
  /** Declenche l'action : le chassis ouvre sa fenetre si elle en porte une. */
  onRunAction: (action: ScreenAction) => void;
}): JSX.Element {
  const baseId = useId();
  const menuId = `${baseId}-record-menu`;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuHostRef = useRef<HTMLSpanElement | null>(null);

  // Un clic dehors referme le menu. Meme regle que les popovers d'outils : le
  // clic est juge par appartenance a l'ancre, pas par cible exacte.
  //
  // ECHAP AUSSI, ET EN PHASE DE CAPTURE SUR `document`. `useScreen` ecoute le
  // meme Echap en BULLE pour fermer la FICHE : une coupure posee sur le groupe
  // ne joue que si le focus est reste dedans, ce qui n'est pas le cas des
  // qu'on a tabule hors du groupe, ni sous Safari macOS ou un clic sur un
  // bouton ne le focalise pas. Le premier Echap fermait alors la fiche en
  // laissant le panneau ouvert : une frappe, le mauvais effet. La capture sur
  // `document` passe AVANT toute ecoute en bulle et `stopPropagation` empeche
  // celle-ci de s'executer, quel que soit l'element focalise.
  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (event: MouseEvent) => {
      const host = menuHostRef.current;
      if (host != null && event.target instanceof Node && host.contains(event.target)) return;
      setMenuOpen(false);
    };
    const onDocKeyCapture = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setMenuOpen(false);
    };
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onDocKeyCapture, true);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onDocKeyCapture, true);
    };
  }, [menuOpen]);

  // Le rang n'est pas resolu (entree a froid par un lien profond) : ".. / .."
  // et les deux fleches eteintes, JAMAIS un rang invente.
  const position = cursor.ready ? `${cursor.index + 1} / ${cursor.total}` : '.. / ..';
  const pending = "Position en cours de resolution : la collection n'est pas encore chargee.";

  // LA LARGEUR RESERVEE DU RANG : la plus longue chaine que la collection
  // puisse produire, soit deux nombres de `digits` chiffres plus les trois
  // signes de " / ", plus les deux `px-1.5` (la boite est en border-box). En
  // chasse fixe, `1ch` vaut l'avance d'un caractere. Plancher a 2 chiffres :
  // il couvre le ".. / .." de l'entree a froid, ou `total` vaut encore 0.
  const digits = Math.max(2, String(cursor.total).length);

  // LES DEUX FLECHES S'ETEIGNENT PAR `aria-disabled`, JAMAIS PAR `disabled` :
  // meme raison que le bouton de partage plus bas, un bouton `disabled` ne
  // recoit aucun evenement de souris (son `title` ne s'affiche donc jamais) et
  // sort de l'ordre de tabulation. Une fleche eteinte doit dire POURQUOI elle
  // l'est (G16) : au bout de la collection, en attente de chargement, ou sans
  // deplacement branche. Le geste, lui, est garde dans le `onClick`.
  const prevOff = onNavigate == null || cursor.prevId == null;
  const nextOff = onNavigate == null || cursor.nextId == null;
  const offReason = (edge: string): string =>
    onNavigate == null
      ? "le deplacement dans la collection n'est pas branche sur cet ecran"
      : cursor.ready
        ? edge
        : "la collection n'est pas encore chargee";
  const prevLabel = prevOff
    ? `Element precedent : ${offReason('premier element de la collection, rien avant')}`
    : 'Element precedent (Alt + fleche haut)';
  const nextLabel = nextOff
    ? `Element suivant : ${offReason('dernier element de la collection, rien apres')}`
    : 'Element suivant (Alt + fleche bas)';

  return (
    <div
      role="group"
      aria-label="Fiche"
      className="flex h-8 shrink-0 items-center gap-0.5"
      onBlur={(event) => {
        // Un panneau ne survit pas au focus qui s'en va : tabuler hors du
        // groupe le referme. `relatedTarget` nul (clic dans le vide) ferme
        // aussi, le clic dehors s'en chargeait de toute facon.
        if (!menuOpen) return;
        const next = event.relatedTarget;
        if (next instanceof Node && event.currentTarget.contains(next)) return;
        setMenuOpen(false);
      }}
    >
      <button
        type="button"
        title={prevLabel}
        aria-label={prevLabel}
        aria-disabled={prevOff || undefined}
        onClick={
          prevOff
            ? undefined
            : () => {
                if (cursor.prevId != null) onNavigate?.(cursor.prevId);
              }
        }
        className={cn(
          RECORD_NAV_BUTTON,
          // Premier ou dernier element : la fleche correspondante s'ETEINT,
          // elle ne disparait pas. On doit savoir qu'on est au bout.
          prevOff
            ? 'cursor-default text-ink-300'
            : 'cursor-pointer hover:bg-surface-3 hover:text-ink-900',
        )}
      >
        {/* Fleches PLEINES du fondateur (`left.svg` / `right.svg`), fut et
            tete, jamais des chevrons, et dessinees dans le bon sens : aucune
            rotation a poser. La puce de sortie, elle, porte `left_back.svg` :
            un chevron SANS fut, du meme alphabet. Se DEPLACER dans le jeu et en
            SORTIR restent deux gestes distincts, mais c'est le FUT qui les
            separe, pas un emprunt a un autre set. */}
        <IconLeft size={14} />
      </button>
      <span
        className="flex h-8 shrink-0 items-center justify-center whitespace-nowrap px-1.5 font-mono text-xs font-medium tabular-nums text-ink-500"
        style={{ minWidth: `calc(${digits * 2 + 3}ch + 0.75rem)` }}
        {...(cursor.ready ? {} : { title: pending })}
      >
        {position}
      </span>
      <button
        type="button"
        title={nextLabel}
        aria-label={nextLabel}
        aria-disabled={nextOff || undefined}
        onClick={
          nextOff
            ? undefined
            : () => {
                if (cursor.nextId != null) onNavigate?.(cursor.nextId);
              }
        }
        className={cn(
          RECORD_NAV_BUTTON,
          nextOff
            ? 'cursor-default text-ink-300'
            : 'cursor-pointer hover:bg-surface-3 hover:text-ink-900',
        )}
      >
        <IconRight size={14} />
      </button>
      <span aria-hidden className="mx-2 h-4 w-px shrink-0 bg-border" />
      {/* LE PARTAGE, INERTE dans ce lot : rien n'est branche derriere. Il porte
          donc sa raison (G16) et reste survolable au doigt comme au clavier.
          `aria-disabled` et NON `disabled` : un bouton `disabled` ne recoit
          aucun evenement de souris, son `title` ne s'affiche donc jamais, et
          un controle desactive muet est exactement ce que G16 interdit. Meme
          procede que les entrees grisees du selecteur de vues. */}
      <button
        type="button"
        aria-disabled
        title="Partager cette fiche : pas encore disponible"
        aria-label="Partager cette fiche : pas encore disponible"
        className={cn(RECORD_NAV_BUTTON, 'cursor-default text-ink-300')}
      >
        {/* La feuille ouverte sur le noeud de partage (`share-file.svg`) : le
            partage d'une FICHE, pas le partage universel. */}
        <IconShareFile size={14} />
      </button>
      <span ref={menuHostRef} className="relative flex">
        {/* LE MENU DES GESTES RARES, rempli par `recordMenuActions` au chantier 2_0
            (Archiver / Desarchiver et Supprimer, decision du fondateur du
            07/08/2026 : ces gestes sont rares, un menu est leur place, la
            rangee 3a porte ce qu'on fait souvent). Il S'OUVRE meme vide, et son
            panneau DIT alors pourquoi : un controle mort et muet est interdit
            (G16), un controle qui ne peut pas s'ouvrir ne se rend pas. */}
        <button
          type="button"
          title="Autres actions"
          aria-label="Autres actions"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-controls={menuId}
          onClick={() => setMenuOpen((open) => !open)}
          className={cn(
            RECORD_NAV_BUTTON,
            'cursor-pointer hover:bg-surface-3 hover:text-ink-900',
            menuOpen && 'bg-surface-3 text-ink-900',
          )}
        >
          {/* Le "..." NU : trois carres PLEINS, largement espaces. C'est le
              glyphe que le prototype REND (`full_spaced.svg`, mesure dans son
              balisage), alors que sa prose annonce `menu.svg`. Les deux
              existent au set et ne se ressemblent pas : `menu.svg` porte des
              carres EVIDES (un carre creux dans chaque) et son vide est deux
              fois plus serre, il se lit "grille", pas "et le reste". Aucun des
              deux n'est jointif : c'est la LARGEUR DU VIDE qui les separe, pas
              sa presence. Le fichier qui REND fait foi ; ARBITRAGE OUVERT :
              une ligne a changer ici si le fondateur voulait bien `IconMenu`,
              en sachant qu'une grille dirait "changer de vue" a l'endroit
              exact ou le niveau collection change de vue. */}
          <IconFullSpaced size={14} />
        </button>
        {menuOpen && (
          /* Meme vocabulaire que le popover des outils (--shadow-pop,
             --radius-md, items de 2rem), mais `role="menu"` et non
             `listbox` : ici on declenche des gestes, on ne choisit pas un
             etat, et il n'y a pas d'entree de sortie. C'est ce qui l'empeche
             de reutiliser `ToolPopover` tel quel. */
          <div
            id={menuId}
            role="menu"
            aria-label="Autres actions"
            className={cn(
              'absolute right-0 top-[calc(100%+0.4375rem)] z-20 flex min-w-48 flex-col p-1',
              'rounded-md border border-border bg-surface shadow-pop',
            )}
          >
            {actions.length === 0 ? (
              <button
                type="button"
                role="menuitem"
                aria-disabled
                title="Cet ecran ne declare aucun geste sur la fiche."
                className="flex h-8 cursor-default items-center rounded-sm px-2 text-left font-sans text-[0.8125rem] font-medium text-ink-300"
              >
                Aucune action pour l&apos;instant
              </button>
            ) : (
              actions.map((action) => {
                /* UNE ACTION ETEINTE RESTE VISIBLE AVEC SA RAISON (G16, G18) :
                   une porte qui ne sait pas supprimer rend l'entree grisee, pas
                   absente. Un geste qui apparait et disparait selon l'app
                   fabrique exactement le "pourquoi je ne peux pas supprimer
                   ici" qu'on veut eviter.
                   `aria-disabled` et NON `disabled`, comme partout dans ce
                   fichier : un bouton `disabled` ne recoit aucun evenement de
                   souris, son `title` ne s'affiche donc jamais, et il sort de
                   l'ordre de tabulation. Sa raison serait inatteignable, ce qui
                   est exactement ce que G16 interdit.
                   AUCUN ROUGE ICI, "Supprimer" compris : le rouge est le
                   DERNIER geste d'un chemin, il ne vit que dans la fenetre de
                   confirmation. En surface, il attirerait le clic qu'il devrait
                   retenir. */
                const off = action.disabled === true;
                const label = off && action.disabledReason != null
                  ? `${action.label} : ${action.disabledReason}`
                  : action.label;
                return (
                  <button
                    key={action.id}
                    type="button"
                    role="menuitem"
                    aria-disabled={off || undefined}
                    title={label}
                    aria-label={label}
                    onClick={
                      off
                        ? undefined
                        : () => {
                            setMenuOpen(false);
                            onRunAction(action);
                          }
                    }
                    className={cn(
                      'flex h-8 items-center gap-2 rounded-sm px-2 text-left font-sans text-[0.8125rem] font-medium transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
                      off
                        ? 'cursor-default text-ink-300'
                        : 'cursor-pointer text-ink-700 hover:bg-surface-1 hover:text-ink-900',
                    )}
                  >
                    {action.icon != null && (
                      <span className="flex shrink-0 items-center justify-center">
                        {action.icon}
                      </span>
                    )}
                    <span className="min-w-0 flex-1 truncate">{action.label}</span>
                  </button>
                );
              })
            )}
          </div>
        )}
      </span>
    </div>
  );
}

/* ========================================================================== */
/* LA BARRE DE LOT : LE TROISIEME ETAT DE LA RANGEE 3 (chantier 3_0)         */
/* ========================================================================== */

/**
 * UN GESTE DE LOT EN COURS, GELE au moment du clic : le verbe, les ids exacts,
 * et la primitive A DOCUMENT UNIQUE sur laquelle `BulkRunner` va boucler.
 *
 * LA PRIMITIVE VOYAGE AVEC LE LOT, elle n'est pas relue au moment d'ecrire :
 * c'est ce qui garantit qu'on ecrit avec le verbe de la porte que
 * l'utilisateur avait sous les yeux quand il a confirme. Meme regle que
 * l'action gardee dans `confirming`.
 */
interface BulkJob {
  kind: 'archive' | 'remove';
  ids: string[];
  runOne(id: string): Promise<void>;
  /**
   * LE NOM DE CHAQUE FICHE DU LOT, LUI AUSSI GELE AU CLIC, et c'est le point.
   * Le compte rendu doit NOMMER les fiches tombees ; or a la fin de l'ecriture
   * les fiches ECRITES ont deja quitte la vue (elles sont archivees, ou elles
   * ne passent plus le filtre). Un nom relu apres coup retomberait sur l'id,
   * pile pour les lignes qu'il faut pouvoir rattraper.
   */
  labelOf(id: string): string;
}

/**
 * LE COMPTE S'ACCORDE. On ecrit ce qu'on dirait A VOIX HAUTE, jamais
 * "N element(s) selectionne(s)" : le "(s)" est une economie de developpeur que
 * personne ne prononce, et elle se lit dans un produit qui parle francais
 * partout ailleurs.
 */
function selectionLabel(count: number): string {
  return count > 1 ? `${count} elements selectionnes` : `${count} element selectionne`;
}

/**
 * LA SORTIE DE LA SELECTION, un seul texte pour les deux chemins (la croix et
 * la touche). `Echap` est NOMMEE ICI ET NULLE PART AILLEURS : le prototype
 * valide ne dessine aucune puce `kbd` dans cette barre, et il fait foi sur le
 * rendu. La touche reste donc annoncee la ou G16 la veut, sur le controle qui
 * fait le geste, sans ajouter un cinquieme objet a une barre que le fondateur a
 * validee a QUATRE controles.
 */
const CLEAR_SELECTION_LABEL = 'Annuler la selection (Echap)';

/**
 * LA BOITE DES BOUTONS DE LA RANGEE 3, DANS SES DEUX ETATS : la barre de lot
 * ici, et la rangee 3a dans le corps de `ScreenLayout` (`actionButtonClasses`,
 * qui vaut cette constante-ci et RIEN D'AUTRE).
 *
 * ELLES NE SONT PLUS JUMELLES, ELLES SONT LA MEME. Un commentaire ne tient pas
 * un invariant : que l'une des deux chaines bouge seule, et la rangee 3 change
 * de hauteur entre son etat collection et son etat lot, donc toute la zone 5
 * saute d'un cran au premier clic dans une case a cocher. Le partage rend
 * l'accident impossible, et il ne coute qu'une ligne.
 */
const BULK_BUTTON = 'h-8 shrink-0 rounded-sm px-3 text-[0.8125rem]';

/**
 * LA HACHURE DE CE QUI EST HORS SERVICE PENDANT UN LOT : le TIROIR (zone 4) et
 * la RECHERCHE (zone 2b). Le nom dit son premier usage, la regle vaut pour les
 * deux : ce qui changerait le JEU sous un lot en cours se hachure, il ne
 * disparait pas.
 *
 * UN CALQUE UNIQUE, EN `::after` : il GRISE, il RAYE et il BLOQUE LES CLICS.
 * Un seul objet pour trois roles ; trois objets se seraient desynchronises au
 * premier ajustement. Le contenu passe DESSOUS a 0.35 d'opacite : attenue,
 * encore lisible, parce qu'on doit pouvoir relire le filtre qui explique la
 * liste sur laquelle on est en train d'agir.
 *
 * AUCUNE COULEUR EN DUR : le trait est `--border-strong`, le vide est
 * `transparent`, -45 degres, 5px de vide (0.3125rem) pour 1px de trait
 * (jusqu'a 0.375rem). Le theme sombre suit tout seul.
 *
 * CES CLASSES SONT LUES PAR TAILWIND DANS CE FICHIER, telles quelles : les
 * couper en morceaux calcules les ferait disparaitre de la feuille de style
 * sans qu'aucun build ne rougisse, et la hachure ne se verrait plus.
 */
const DRAWER_HATCH =
  "relative [&>*]:opacity-35 after:absolute after:inset-0 after:z-[1] after:content-[''] " +
  'after:bg-[image:repeating-linear-gradient(-45deg,transparent_0_0.3125rem,var(--border-strong)_0.3125rem_0.375rem)]';

/**
 * CE QUE LA HACHURE NE DIT PAS TOUTE SEULE.
 *
 * Les rayures disent qu'il se passe QUELQUE CHOSE ; elles ne disent ni QUOI, ni
 * pour COMBIEN DE TEMPS, ni COMMENT revenir. Sans ces deux phrases, cinq clics
 * (un tag, sa croix, "Effacer filtres", une pastille de favori, le champ de
 * recherche) ne produisent strictement rien et aucun ne s'explique : on ne sait
 * pas si c'est hors service ou casse.
 *
 * ELLES NE NOMMENT PAS `Echap`, et c'est voulu : la touche est nommee a UN seul
 * endroit du produit, sur le controle qui fait le geste (voir
 * `CLEAR_SELECTION_LABEL`). Ici on nomme la SORTIE, pas la touche.
 *
 * OU ELLES SE POSENT : sur la coque EXTERIEURE, celle qui reste survolable,
 * jamais sur le contenu hachure. Le calque bloque la souris et `inert` bloque
 * le clavier : un `title` pose dessous ne serait jamais lu.
 */
const DRAWER_OFF_REASON =
  "Le tiroir est hors service tant qu'une selection est en cours. " +
  'Annule la selection pour filtrer a nouveau.';
/**
 * POURQUOI LES TROIS GESTES DE LA BARRE S'ETEIGNENT quand un lot est deja
 * ouvert (voir `bulkBarAvailability`). Elle nomme ce qui est A L'ECRAN, parce
 * que c'est la fenetre ouverte qui porte la suite du geste : deux boucles
 * lancees sur la meme selection ecriraient les memes documents deux fois, et le
 * second compte rendu effacerait le premier, sa liste d'echecs comprise.
 */
const BULK_BUSY_REASON =
  "Un geste de lot est deja en cours. Reponds a la fenetre ouverte, puis recommence.";
/** Le pendant pour la recherche (2b). Meme regle, meme forme, autre zone. */
const SEARCH_OFF_REASON =
  "La recherche est hors service tant qu'une selection est en cours. " +
  'Annule la selection pour chercher a nouveau.';

/**
 * UN GESTE DE LOT. Il est GRISE AVEC SA RAISON quand la porte ne sait pas le
 * faire, jamais absent (G16).
 *
 * LA RAISON EST PORTEE PAR UNE COQUE, PAS PAR LE BOUTON. Le `Button` du socle
 * porte `disabled:pointer-events-none` : desactive, il ne recoit aucun
 * evenement de souris, donc son propre `title` ne s'afficherait jamais. Meme
 * procede que le bouton "+ Ajouter" de la rangee 3a, quelques centaines de
 * lignes plus bas ; le bouton garde la raison en `aria-label` pour le clavier
 * et les lecteurs d'ecran.
 */
function BulkAction({
  label,
  variant,
  reason,
  onClick,
}: {
  label: string;
  variant: 'primary' | 'secondary' | 'danger';
  /** `null` = disponible. Une chaine = la raison, et le bouton s'eteint. */
  reason: string | null;
  onClick: () => void;
}): JSX.Element {
  const off = reason != null;
  return (
    <span title={reason ?? label} className="flex shrink-0">
      <Button
        variant={variant}
        size="sm"
        className={BULK_BUTTON}
        aria-label={reason ?? label}
        onClick={onClick}
        disabled={off}
      >
        {label}
      </Button>
    </span>
  );
}

/**
 * LA BARRE DE LOT, le TROISIEME etat de la rangee 3 (apres collection et
 * fiche). Des qu'une ligne est cochee, la rangee change de NATURE et se vide
 * ENTIEREMENT : plus d'actions de porte (3a), plus d'outils (3b), et plus de
 * selecteur de vues (3c).
 *
 * CE DERNIER POINT CONTREDIT G18 ("epingle, jamais conditionnel") ET C'EST
 * VOULU : arbitrage du fondateur du 07/08/2026, le selecteur s'efface avec le
 * reste. On n'est plus en train de REGARDER une collection, on est en train
 * d'AGIR sur un lot, et changer de vue au milieu du geste ferait tomber la
 * selection (elle n'existe qu'en tableau). La doctrine porte l'exception, ce
 * n'est pas un oubli a "reparer".
 *
 * ALIGNEE A GAUCHE, PAS CENTREE : un groupe centre aurait invente un troisieme
 * alignement dans un ecran qui n'en connait que deux.
 *
 * EXACTEMENT QUATRE CONTROLES, et le premier n'en est pas un :
 *
 *   [ 3 elements selectionnes  x ]  [ Modification groupee ]  [ Archiver ]  [ Supprimer ]
 *
 * - LE COMPTE EST UN TEXTE, en encre. Il ne se clique pas, il s'ANNONCE
 *   (`role="status"`). Le premier essai en faisait un bouton portant aussi la
 *   croix : trop lourd, et ca faisait du compte un controle.
 * - LA CROIX EST LE SEUL CONTROLE de ce groupe, et elle est NUE comme les
 *   autres commandes du gabarit ("Effacer filtres", "Garder") : les coques
 *   bordees appartiennent aux OBJETS, pas aux GESTES.
 * - "MODIFICATION GROUPEE" EST PRIMAIRE, EN APLAT LIME, et LA REGLE DU LIME
 *   UNIQUE TIENT QUAND MEME : cet etat est le seul ou le bouton "+ Ajouter" a
 *   QUITTE l'ecran, donc le lime n'est pas duplique, il est TRANSFERE au lot.
 *   A NE PAS "REPARER" en retrogradant ce bouton.
 *   Le compte, lui, a ete essaye en lime sous trois formes et repose : aucun
 *   lime du set ne porte du texte de facon lisible (1.3:1 pour `--accent`,
 *   1.9:1 pour `--accent-dot`, 2.6:1 pour `--accent-soft-ink`, tous sous le
 *   seuil), et le seul lisible est l'aplat, qui ferait du compte un objet.
 * - "SUPPRIMER" N'EST PAS ROUGE PLEIN ICI : il porte la variante `danger` du
 *   socle, qui est en CONTOUR (bordure `--danger-border`, encre `--danger`,
 *   fond `--danger-tint` au survol). Le rouge PLEIN est garde pour le bouton de
 *   confirmation de la fenetre, ou il est le DERNIER geste et non le premier :
 *   un aplat rouge dans une barre attire le clic qu'il devrait retenir.
 */
function BulkBar({
  count,
  availability,
  onClear,
  onEdit,
  onArchive,
  onRemove,
}: {
  count: number;
  availability: { archive: string | null; remove: string | null; edit: string | null };
  /** Absent : la croix s'eteint avec sa raison, elle ne disparait pas. */
  onClear?: () => void;
  onEdit: () => void;
  onArchive: () => void;
  onRemove: () => void;
}): JSX.Element {
  // UN ETAT SANS SORTIE EST UNE NASSE. Si le moteur n'a pas branche
  // l'annulation, la croix reste VISIBLE et DIT pourquoi elle ne repond pas
  // (G16) : la faire disparaitre laisserait l'utilisateur enferme dans un etat
  // dont plus rien a l'ecran ne nomme la sortie.
  const clearOff = onClear == null;
  const clearLabel = clearOff
    ? "Cet ecran ne sait pas annuler la selection. Decoche les lignes une a une."
    : CLEAR_SELECTION_LABEL;
  return (
    <div className="flex min-w-0 flex-1 items-center gap-3">
      <span className="flex shrink-0 items-center gap-1">
        {/* LE COMPTE. `role="status"` : il s'annonce tout seul quand il change,
            personne n'a a aller le chercher. Chasse tabulaire pour que passer
            de 9 a 10 ne fasse pas glisser la croix d'un demi-caractere. */}
        <span
          role="status"
          className="whitespace-nowrap font-sans text-[0.8125rem] font-semibold tabular-nums text-ink-900"
        >
          {selectionLabel(count)}
        </span>
        {/* LA CROIX, NUE. Meme anatomie que les gestes du tiroir : ni contour
            ni fond, un survol, un anneau au focus. Elle S'ETEINT par
            `aria-disabled` et JAMAIS par `disabled` : un bouton `disabled` ne
            recoit aucun evenement de souris, son `title` ne s'afficherait donc
            jamais, et il sort de l'ordre de tabulation. Sa raison serait
            inatteignable, exactement ce que G16 interdit. */}
        <button
          type="button"
          onClick={clearOff ? undefined : onClear}
          aria-disabled={clearOff || undefined}
          title={clearLabel}
          aria-label={clearLabel}
          className={cn(
            'flex h-6 w-6 shrink-0 items-center justify-center rounded-pill border-none bg-transparent p-0',
            'transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
            clearOff
              ? 'cursor-default text-ink-300'
              : 'cursor-pointer text-ink-400 hover:bg-surface-3 hover:text-ink-900',
          )}
        >
          <IconClose size={12} />
        </button>
      </span>
      <BulkAction
        label="Modification groupee"
        variant="primary"
        reason={availability.edit}
        onClick={onEdit}
      />
      <BulkAction
        label="Archiver"
        variant="secondary"
        reason={availability.archive}
        onClick={onArchive}
      />
      <BulkAction
        label="Supprimer"
        variant="danger"
        reason={availability.remove}
        onClick={onRemove}
      />
    </div>
  );
}

/**
 * Gabarit d'ecran standard MyTaki (gabarit v2, habillage plat).
 *
 * Squelette : rail gauche fixe (identite + portes, rien d'autre) + colonne
 * principale (en-tete 3 colonnes, rangee de commandes, tiroir, contenu).
 * Zones separees par des filets `border-soft` sur une surface blanche
 * continue. Seule la zone 5 defile.
 *
 * @example
 * <ScreenLayout
 *   appName="Inventaire"
 *   appIcon={<IconModule size={18} />}       // puce en tete du rail (1a)
 *   menuItems={[{ id: 'materiel', label: 'Materiel', count: 30 }]}
 *   activeMenuId={screen.section ?? 'materiel'} onMenuSelect={screen.setSection}
 *   title="Materiel"
 *   headerStats={screen.door.stats}
 *   onSearchChange={screen.setSearch} searchValue={screen.query.search}
 *   searchPlaceholder={screen.door.searchPlaceholder}
 *   quickActions={screen.door.actions}       // rangee 3a, regle "+ Ajouter"
 *   tools={screen}                           // outils 3b + tiroir 4, tels quels
 *   sortOptions={[{ key: 'nom', label: 'Nom' }]}
 *   views={screen.views} activeViewId={screen.viewId} onViewSelect={screen.setView}
 * >
 *   ...contenu (ViewHost)...
 * </ScreenLayout>
 */
export function ScreenLayout({
  appName,
  appIcon,
  menuItems = [],
  activeMenuId,
  onMenuSelect,
  title,
  headerStats = [],
  onSearchChange,
  searchValue,
  searchPlaceholder = DEFAULT_SEARCH_PLACEHOLDER,
  quickActions = [],
  viewActions = [],
  tools,
  sortOptions = [],
  filters = [],
  activeFilterId,
  onFilterSelect,
  views = [],
  activeViewId,
  onViewSelect,
  // Sortie de la FICHE (rangee 3). A ne pas confondre avec `onBack` du
  // ScreenNavContext, lu quelques lignes plus bas, qui sort de l'APP entiere
  // depuis la puce d'icone de la zone 1a.
  onRecordExit,
  recordExitLabel = 'Retour',
  saveState,
  onRetrySave,
  recordCursor,
  onRecordNavigate,
  recordMenuActions,
  scrollRef,
  loading = false,
  skeleton,
  error = null,
  errorTitle,
  onRetry,
  retryLabel = 'Retry',
  empty = false,
  emptyTitle = 'Nothing here yet',
  emptyHint,
  children,
}: ScreenLayoutProps): JSX.Element {
  const hasAppIdentity = appName != null;
  const { onBack, backLabel } = useContext(ScreenNavContext);
  const hasSearch = onSearchChange != null;

  // Le NIVEAU se DEDUIT, il ne se demande pas : `views` porte le level de chaque
  // entree et `activeViewId` dit laquelle est active. Une prop "niveau" serait
  // une seconde source de verite, donc une occasion de mentir, et chaque app
  // aurait a la calculer (exactement le bug du bouton "Modifier" re-code
  // partout). La garde explicite documente le cas "ecran sans vues".
  const isRecordLevel =
    activeViewId != null &&
    views.some((view) => view.id === activeViewId && view.level === 'record');

  // LE SELECTEUR (3c) NE PORTE QUE LE NIVEAU COLLECTION. L'entree "Fiche" en a
  // ete retiree, et le filet qui separait les deux groupes est parti avec elle :
  // on n'entre pas dans une fiche en CHOISISSANT une vue, on entre en OUVRANT
  // une ligne. Un selecteur qui propose "Fiche" sans dire LAQUELLE promet un
  // geste qu'il ne peut pas tenir : ou il ouvre une fiche arbitraire, ou il ne
  // fait rien.
  // LE FILTRAGE EST AU RENDU, PAS A LA SOURCE : `views` garde ses entrees
  // `record` (c'est `isRecordLevel` juste au-dessus, et `lastCollectionViewId`
  // cote moteur, qui les lisent), le selecteur n'en montre plus aucune. Sur une
  // fiche, AUCUNE entree n'est donc active : c'est la puce de sortie qui dit ou
  // l'on retombe.
  const collectionViews = views.filter((view) => view.level === 'collection');

  // Titre (zone 2a) : le label de l'entree de menu active prime, `title` sert
  // de repli (ecran sans menu, ou id actif inconnu).
  const activeMenuItem = menuItems.find((item) => item.id === activeMenuId);
  const headerTitle = activeMenuItem != null ? activeMenuItem.label : title;

  // Zone 2c : plafond de 3 stats, tenu par le gabarit (doctrine de la porte).
  const stats = headerStats.slice(0, 3);
  const showStats = stats.length > 0;

  // Champ de recherche : controle par l'app si `searchValue` est fourni, sinon
  // le gabarit tient un etat interne de repli (le champ reste fonctionnel meme
  // si l'app oublie `searchValue`), tout en remontant chaque frappe a l'app.
  const searchControlled = searchValue !== undefined;
  const [internalSearch, setInternalSearch] = useState('');
  const searchFieldValue = searchControlled ? searchValue : internalSearch;

  /* ---- Zones 3b + 4 : l'etat des outils, lu depuis le moteur ---- */

  // `sections: false` = la porte ne se tranche pas : le CATALOGUE de filtres,
  // l'etoile et le tiers des RACCOURCIS (4b + 4c, filet compris) sont masques.
  // L'outil FILTRE, lui, reste RENDU depuis le chantier 2_0 : il porte
  // l'entree Archives (G20). Le tiroir ne se rend alors que le temps du mode,
  // avec le seul tag de mode.
  const doorSections = tools?.door?.sections !== false;
  const toolFilters = tools?.filters ?? [];
  const activeFilterIds = tools?.activeFilterIds ?? [];
  const sortStack = tools?.sortStack ?? [];
  const favorites = tools?.favorites ?? [];
  const drawerOpen = tools?.drawerOpen ?? false;
  const canKeep = tools?.canKeepFavorite ?? false;
  const archivesMode = tools?.archivesMode ?? false;

  /* ---- La selection de lignes et la barre de lot (chantier 3_0) ---- */

  const selection = tools?.selection ?? [];
  const writeCell = tools?.writeCell;
  const archiveOne = tools?.archiveOne;
  const removeOne = tools?.removeOne;

  /*
   * `bulkActive` : LA RANGEE 3 EST-ELLE DANS SON TROISIEME ETAT.
   *
   * `selectionLevel` SERT A REFUSER, JAMAIS A ALLUMER, et la nuance vaut la
   * peine d'etre lue. La selection n'existe QU'EN TABLEAU (arbitrage du
   * fondateur du 07/08/2026) et quitter le tableau l'ANNULE : c'est le moteur
   * qui le fait, comme il l'annule deja au changement de porte, de recherche,
   * de filtre ou de mode. Le chassis pourrait s'en assurer une seconde fois en
   * exigeant `selectionLevel === 'table'` ; il ne le fait PAS, parce qu'un
   * champ ABSENT eteindrait alors la barre entiere en silence, sans qu'aucun
   * build ne rougisse. C'est exactement la panne du chantier 2_0, ou le mode
   * Archives est parti en relecture avec une adresse entierement inerte.
   * On lit donc `!== null` : le moteur qui dit EXPLICITEMENT "aucune surface ne
   * peut choisir" est cru, celui qui ne dit rien ne bloque personne.
   *
   * `!isRecordLevel` : une fiche ne porte pas de selection. Si le moteur ouvre
   * une fiche avec des lignes encore cochees, la rangee 3 est celle de la
   * fiche, pas celle du lot.
   */
  const bulkActive =
    !isRecordLevel && selection.length > 0 && tools?.selectionLevel !== null;

  /*
   * CE QUE LA PORTE SAIT FAIRE, avec un FILET DE SECURITE. `bulkAvailability`
   * appartient au moteur ; mais un geste dont la primitive n'existe pas ne doit
   * pas se proposer et ne rien faire au clic. Un bouton muet est pire qu'un
   * bouton eteint : on croit avoir agi. La raison de repli est ecrite POUR UN
   * UTILISATEUR, comme celles du moteur.
   */
  const declaredAvailability = tools?.bulkAvailability;
  /*
   * LE PLAFOND SE DIT AVANT LA CONFIRMATION, JAMAIS APRES.
   *
   * `BulkRunner` refuse deja un lot trop grand, mais il le refuse dans une
   * fenetre qui ne s'ouvre qu'APRES la confirmation destructrice : on lisait
   * "Supprimer definitivement 350 fiches ? C'est definitif", on cliquait le
   * seul bouton rouge plein du produit, et la fenetre suivante repondait "Lot
   * trop grand, rien n'a ete ecrit". Le dernier geste irreversible du produit
   * franchi pour rien.
   *
   * ET LE PIEGE S'ATTEINT EN UN CLIC : la case d'en-tete du tableau coche tout
   * le jeu AFFICHE, et la fenetre cliente en tient jusqu'a 500, soit plus du
   * double du plafond. Les TROIS gestes s'eteignent donc AVEC LEUR RAISON
   * (G16), avant toute fenetre ; le refus cote fenetre reste, en dernier filet.
   *
   * LA MEME RAISON SUR LES TROIS, meme quand la porte a deja la sienne : tant
   * que le lot est trop grand, c'est CA qu'il faut corriger, et le reste se
   * dira tout seul une fois la selection reduite.
   */
  const overBulkMax = selection.length > BULK_MAX;
  const bulkMaxReason = `Un lot ne depasse pas ${BULK_MAX} fiches. Tu en as choisi ${selection.length} : reduis la selection.`;
  const bulkAvailability = overBulkMax
    ? { edit: bulkMaxReason, archive: bulkMaxReason, remove: bulkMaxReason }
    : {
        edit:
          declaredAvailability?.edit ??
          (writeCell == null ? "Cet ecran ne sait pas modifier des fiches en lot." : null),
        archive:
          declaredAvailability?.archive ??
          (archiveOne == null ? "Cet ecran ne sait pas archiver une fiche." : null),
        remove:
          declaredAvailability?.remove ??
          (removeOne == null ? "Cet ecran ne sait pas supprimer une fiche." : null),
      };

  /*
   * LES GESTES DE FICHE : `tools` EST LE DEFAUT, LA PROP EST LA SURCHARGE.
   *
   * C'est ce qui tient la promesse du chantier 2_0, "dans toutes les apps, sur
   * toutes les portes, sans qu'aucune app n'ecrive une ligne". Tant qu'ils
   * n'arrivaient que par la prop, un ecran qui passait deja `tools={screen}` et
   * `recordCursor` sans y penser ouvrait un menu "..." vide la ou l'ecran d'a
   * cote proposait Archiver et Supprimer : meme menu, meme geste, deux
   * vocabulaires selon l'app, exactement le bouton qui apparait et disparait
   * que le brief interdit.
   *
   * `??` et non `||` : un ecran qui fournit DELIBEREMENT un tableau vide veut
   * un menu vide (et son panneau dira pourquoi), il ne veut pas retomber en
   * silence sur les gestes du moteur.
   */
  const menuActions = recordMenuActions ?? tools?.recordMenuActions ?? [];

  // L'OUTIL FILTRE NE SE REND PLUS SOUS CONDITION. C'est la consequence
  // ASSUMEE de l'entree Archives, et un changement de regle, pas un effet de
  // bord : il etait masque quand la porte declarait `sections: false` ou quand
  // son catalogue etait vide ; desormais son catalogue n'est JAMAIS vide,
  // puisque le socle y injecte une entree que toute porte offre. Ce qui reste
  // conditionnel, c'est le CATALOGUE de la porte a l'interieur du popover : sur
  // une porte sans tranches, il ne montre que l'entree Archives.
  const showFilterTool = tools != null;
  const showFilterCatalogue = doorSections && toolFilters.length > 0;
  const showSortTool = tools != null && sortOptions.length > 0;
  const showStarTool = tools != null && doorSections && tools.setDrawerOpen != null;
  const hasToolGroup = showFilterTool || showSortTool || showStarTool;
  // LE TIERS DE DROITE DU TIROIR (4b garder + 4c favoris) SUIT `sections`,
  // EXACTEMENT COMME L'ETOILE DE LA RANGEE 3b. Une porte `sections: false` dit
  // par contrat qu'elle n'a AUCUN favori : le bouton "garder en favori" y
  // proposait de fabriquer un objet que la porte ne saura jamais rendre. Il
  // n'etait garde que par la presence du callback, qui vient du moteur et existe
  // donc toujours ; l'etoile de 3b, elle, avait deja ete masquee pour cette
  // raison. Le trou ne se voyait pas tant que le tiroir ne se rendait pas sur
  // ces portes : le mode archives l'y fait paraitre.
  const showShortcuts = doorSections;
  // LE TIROIR SE REND AUSSI SUR UNE PORTE SANS TRANCHES QUAND LE MODE EST
  // ALLUME. Cette porte n'a pas de tiroir, mais le mode y existe quand meme (il
  // est injecte partout) et il doit s'expliquer : le tag est le seul objet qui
  // dise pourquoi la liste n'est pas celle qu'on attendait. Il redisparait avec
  // le mode. Meme logique que le tiroir qui nait ouvert quand l'adresse
  // d'arrivee pose des filtres : le destinataire doit voir ce qui explique sa
  // liste.
  const showDrawer = tools != null && !isRecordLevel && (doorSections || archivesMode);
  // ET IL EST DEPLOYE, sur cette porte-la, sans attendre `drawerOpen` : sans
  // tranches il n'y a pas d'etoile en 3b, donc aucun geste pour le deplier. Un
  // tag rendu dans un tiroir replie serait un tag que personne ne peut voir.
  // Partout ailleurs, `drawerOpen` decide seul, exactement comme avant.
  const drawerDeployed = drawerOpen || (!doorSections && archivesMode);

  // UN SEUL popover ouvert a la fois. Etat du CHASSIS, pas du moteur : un menu
  // deroulant n'est pas un etat d'ecran, il ne survit ni au changement de
  // porte ni au passage en fiche (l'effet plus bas les referme).
  const [openPop, setOpenPop] = useState<'filter' | 'sort' | null>(null);

  /*
   * LES DEUX FENETRES DU CHASSIS. Elles vivent A LA RACINE de l'arbre rendu,
   * jamais dans la rangee 3 : celle-ci est `relative z-10` et ouvre donc un
   * contexte d'empilement dont un overlay ne sortirait plus.
   *
   * 1. LA FENETRE D'ENTREE DU MODE. Entrer dans un mode n'est pas cocher un
   *    filtre : ca change ENTIEREMENT ce que l'ecran montre, et rien dans un
   *    menu ne peut le faire comprendre en une ligne. Elle ne parait qu'a
   *    l'ENTREE ; SORTIR ne redemande RIEN, c'est reversible et sans
   *    consequence, redemander serait de la ceremonie.
   * 2. LA FENETRE D'UNE ACTION qui porte un `confirm` : c'est le CHASSIS qui
   *    l'ouvre et n'appelle `onClick` qu'apres confirmation.
   *
   * DEUX ETATS PAR FENETRE, ET C'EST VOULU : le Modal s'anime a la FERMETURE,
   * donc l'action confirmee doit rester en place le temps du fondu. On bascule
   * `confirmOpen` et on garde `confirming` : sans ca, la fenetre se viderait de
   * son texte avant de disparaitre.
   */
  const [askArchivesMode, setAskArchivesMode] = useState(false);
  const [confirming, setConfirming] = useState<ScreenAction | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const runRecordAction = (action: ScreenAction): void => {
    if (action.confirm == null) {
      action.onClick();
      return;
    }
    setConfirming(action);
    setConfirmOpen(true);
  };

  /*
   * LES TROIS FENETRES DU LOT, elles aussi A LA RACINE de l'arbre (plus bas).
   *
   * LE LOT EST GELE AU CLIC, ids compris. On agit sur ce que l'utilisateur a
   * VU et CONFIRME, jamais sur un lot recompose entre-temps : la fenetre reste
   * ouverte pendant que l'ecran vit derriere elle (un rechargement, un voisin
   * qui archive), et une confirmation qui porterait "3 fiches" pour en ecrire
   * 5 serait le pire defaut possible de ce chantier. C'est la meme regle que
   * `confirming` juste au-dessus, poussee jusqu'aux ids.
   *
   * DEUX ETATS PAR FENETRE (le lot et l'ouverture), pour la meme raison que
   * `confirming` / `confirmOpen` : le Modal s'anime a la FERMETURE, donc le
   * contenu doit rester en place le temps du fondu, sinon la fenetre se vide
   * de son texte avant de disparaitre.
   */
  const [bulkAsk, setBulkAsk] = useState<BulkJob | null>(null);
  const [bulkAskOpen, setBulkAskOpen] = useState(false);
  const [bulkRun, setBulkRun] = useState<BulkJob | null>(null);
  const [bulkRunOpen, setBulkRunOpen] = useState(false);
  const [bulkEdit, setBulkEdit] = useState<Pick<BulkJob, 'ids' | 'labelOf'> | null>(null);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);

  /*
   * UN GESTE DE LOT NE S'EMPILE PAS SUR UN AUTRE.
   *
   * RIEN NE L'INTERDISAIT, et le chemin est court : le `Modal` du socle pose
   * bien un voile qui avale les CLICS, mais il ne piege pas le FOCUS et ne rend
   * pas le fond inerte. Un `Maj + Tab` depuis la fenetre revient donc sur la
   * barre de lot restee dessous, et `Entree` y ouvre un SECOND geste pendant
   * que le premier ecrit. La boucle en cours changeait alors de verbe et
   * supprimait ce qu'elle devait archiver.
   *
   * DEUX GARDES, ET LES DEUX SONT VOULUES : celle-ci ferme la porte, celle du
   * moteur de lot gele ce qui passerait quand meme.
   *
   * ON LIT LES TROIS DRAPEAUX D'OUVERTURE (`*Open`) ET JAMAIS LES ETATS GARDES
   * (`bulkAsk`, `bulkRun`, `bulkEdit`) : ceux-la survivent au fondu de
   * fermeture, et s'y fier interdirait un second geste parfaitement legitime
   * pendant la demi-seconde qui suit le premier.
   */
  const bulkBusy = bulkAskOpen || bulkRunOpen || bulkEditOpen;

  /*
   * ET LA GARDE SE VOIT, elle n'est pas seulement muette (G16).
   *
   * Le `if (bulkBusy) return;` des gestes ci-dessous suffit a EMPECHER le
   * second lot ; il ne suffit pas a l'EXPLIQUER. Trois boutons qui ne
   * repondent plus a rien sont exactement le "controle mort et muet" que G16
   * interdit, et le voile du `Modal` etant translucide, on les VOIT rester
   * pleins pendant qu'ils ne font plus rien.
   *
   * LES ETEINDRE FERME AUSSI LE CHEMIN, au lieu de le boucher a l'arrivee :
   * `BulkAction` rend un `Button` reellement `disabled`, donc les trois
   * quittent l'ordre de tabulation, et le `Maj + Tab` qui ouvrait ce trou ne
   * tombe plus dessus. La raison, elle, vit sur la COQUE (un element desactive
   * ne recoit aucun evenement de souris, son propre `title` ne s'afficherait
   * jamais).
   *
   * ELLE PASSE DEVANT TOUTES LES AUTRES, plafond compris : tant qu'une fenetre
   * de lot est a l'ecran, c'est elle qu'il faut traiter, et le reste se dira
   * tout seul apres.
   */
  const bulkBarAvailability = bulkBusy
    ? { edit: BULK_BUSY_REASON, archive: BULK_BUSY_REASON, remove: BULK_BUSY_REASON }
    : bulkAvailability;

  /**
   * RELEVE LES NOMS DES LIGNES AFFICHEES, une fois pour toutes, au moment du
   * clic. Les trois gestes de lot s'en servent : leur compte rendu doit NOMMER
   * les fiches tombees, et a la fin de l'ecriture les fiches ECRITES ont quitte
   * la vue (archivees, supprimees, ou sorties du filtre parce que le lot vient
   * de changer le champ sur lequel l'ecran trie). Relire un nom a ce
   * moment-la ne rendrait que des identifiants, pile pour les lignes qu'il faut
   * pouvoir rattraper. `ProjectedRow.title` n'est jamais vide : il retombe deja
   * sur l'id.
   */
  const freezeLabels = (): ((id: string) => string) => {
    const names = new Map((tools?.rows ?? []).map((row) => [row.id, row.title]));
    return (id: string) => names.get(id) ?? id;
  };

  /**
   * Ouvre la CONFIRMATION d'un geste de lot. Les deux fenetres sont celles du
   * chantier 2_0, avec le NOMBRE ("Archiver 12 fiches ?") : meme boite, meme
   * ton, meme ordre de boutons, meme focus par defaut sur Annuler. Deux
   * fenetres differentes pour un meme geste feraient deux vocabulaires, et le
   * lecteur chercherait ce qui change entre les deux au lieu de lire le nombre.
   */
  const askBulk = (kind: BulkJob['kind'], runOne: (id: string) => Promise<void>): void => {
    // UN LOT A LA FOIS (voir `bulkBusy`). Le bouton est deja eteint AVEC SA
    // RAISON quand on passe ici (`bulkBarAvailability`) : cette garde est la
    // ceinture, pas le message. Elle sort donc en silence, il n'y a rien a dire
    // de plus que ce que le bouton dit deja.
    if (bulkBusy) return;
    setBulkAsk({ kind, ids: [...selection], runOne, labelOf: freezeLabels() });
    setBulkAskOpen(true);
  };

  const toolsHostRef = useRef<HTMLDivElement | null>(null);
  const baseId = useId();
  const filterPopId = `${baseId}-filter-pop`;
  const sortPopId = `${baseId}-sort-pop`;
  const drawerId = `${baseId}-pills-wrap`;

  // Escape et clic dehors ferment LES POPOVERS, jamais le tiroir. Le clic est
  // juge par appartenance au groupe d'outils (ref) : un clic dans un popover
  // (multi-choix) ne ferme rien, un clic ailleurs ferme.
  useEffect(() => {
    if (openPop == null) return;
    const onDocClick = (event: MouseEvent) => {
      const host = toolsHostRef.current;
      if (host != null && event.target instanceof Node && host.contains(event.target)) return;
      setOpenPop(null);
    };
    const onDocKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenPop(null);
    };
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onDocKey);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onDocKey);
    };
  }, [openPop]);

  // Changer de porte ou passer en fiche referme les popovers : un menu ouvert
  // sur un catalogue qui vient de changer montrerait des entrees perimees.
  useEffect(() => {
    setOpenPop(null);
  }, [activeMenuId, isRecordLevel]);

  // ENTRER DANS LE LOT REFERME LES POPOVERS, pour la meme raison qu'un
  // changement de porte : la rangee 3 se vide ENTIEREMENT, donc l'outil qui
  // ancrait le panneau n'est plus rendu. Sans ca, `openPop` resterait pose sur
  // un ancrage disparu et le panneau reviendrait, ouvert, a l'annulation de la
  // selection : un menu qu'on n'a pas rouvert.
  useEffect(() => {
    if (bulkActive) setOpenPop(null);
  }, [bulkActive]);

  /*
   * `ECHAP` ANNULE LA SELECTION.
   *
   * OU CETTE ECOUTE SE POSE, ET POURQUOI LA. Trois autres ecoutes d'`Echap`
   * existent deja et l'ordre entre elles a deja coute une correction au
   * chantier 2_0 :
   *
   * 1. `useScreen` ferme la FICHE, en BULLE sur `document`, et son effet n'est
   *    monte QU'AU NIVEAU `record`. La barre de lot, elle, n'existe qu'au
   *    niveau collection : les deux ne peuvent pas se declencher sur la meme
   *    frappe. Rien a arbitrer, mais il fallait le verifier avant d'ecrire.
   * 2. Le menu "..." de la rangee 3c ecoute en CAPTURE avec `stopPropagation`,
   *    justement pour passer AVANT le point 1. Il ne vit lui aussi qu'au niveau
   *    fiche : hors de portee d'ici.
   * 3. Les popovers d'outils (plus haut dans ce fichier) ecoutent en BULLE.
   *    En lot ils sont deja refermes par l'effet ci-dessus, mais l'ordre entre
   *    deux ecoutes de bulle posees sur `document` depend de l'ordre de
   *    MONTAGE : on ne s'y fie pas, on garde explicitement `openPop`.
   *
   * CETTE ECOUTE EST DONC EN BULLE, ET SURTOUT PAS EN CAPTURE. En capture elle
   * passerait AVANT le `Modal`, qui ecoute lui aussi `Escape` sur `document`
   * sans stopper la propagation : une seule frappe fermerait la fenetre de lot
   * ET jetterait la selection dessous. C'est la premiere garde ci-dessous.
   */
  const clearSelectionRef = useRef<(() => void) | undefined>(undefined);
  clearSelectionRef.current = tools?.clearSelection;
  useEffect(() => {
    if (!bulkActive) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      // UN OVERLAY EST OUVERT (une fenetre de lot, la fenetre du mode, un
      // Modal d'app). Il ferme par `Escape` tout seul ; le lot reste, sinon on
      // sortirait d'une fenetre et on perdrait le travail qu'elle preparait.
      // `role="dialog"` est le seul signal commun a tous les overlays du socle,
      // et c'est deja la garde que pose `useScreen`.
      if (document.querySelector('[role="dialog"]') != null) return;
      // ON TAPE DANS UN CHAMP. La recherche 2b, elle, est desormais HORS
      // SERVICE pendant le lot (hachuree et `inert`, zone 2b) : ce qui reste
      // atteignable, c'est un champ de la zone 5 ou d'une app. La, `Echap`
      // annule la saisie ; il ne jette pas le lot par-dessus.
      const active = document.activeElement;
      const tag = active?.tagName.toLowerCase();
      if (tag === 'input' || tag === 'select' || tag === 'textarea') return;
      if (active instanceof HTMLElement && active.isContentEditable) return;
      // Un panneau d'outil est ouvert : il a la touche (voir le point 3).
      if (openPop != null) return;
      event.preventDefault();
      clearSelectionRef.current?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [bulkActive, openPop]);

  // LE FAVORI QU'ON VIENT DE FABRIQUER DOIT SE VOIR. `keepFavorite` ajoute la
  // pastille EN BOUT de liste, et le tiers de droite du tiroir est borne : sur
  // une porte qui declare deja plusieurs presets, la nouvelle pastille nait
  // hors du cadre visible, et comme la barre de defilement macOS ne se montre
  // qu'au geste, rien ne dit qu'il faut faire defiler. Le bouton se grise, et
  // c'est le seul retour du geste. On ramene donc le defilement en bout quand
  // la liste GRANDIT, et seulement dans ce cas : en retirer un ne doit pas
  // deplacer ce qu'on regarde.
  const favoritesScrollRef = useRef<HTMLDivElement | null>(null);
  const favoritesCountRef = useRef(favorites.length);
  useEffect(() => {
    const grew = favorites.length > favoritesCountRef.current;
    favoritesCountRef.current = favorites.length;
    if (!grew) return;
    const node = favoritesScrollRef.current;
    if (node != null) node.scrollLeft = node.scrollWidth;
  }, [favorites.length]);

  /* ---- Les entrees des popovers ---- */

  const filterItems: ToolPopoverItem[] = toolFilters.map((filter) => ({
    id: filter.id,
    label: filter.label,
    active: activeFilterIds.includes(filter.id),
    ...(filter.count != null ? { detail: String(filter.count) } : {}),
  }));

  const sortItems: ToolPopoverItem[] = sortOptions.map((option) => {
    const at = sortStack.findIndex((level) => level.key === option.key);
    const active = at !== -1;
    return {
      id: option.key,
      label: option.label,
      active,
      // Le rang n'est ecrit que quand la pile a plusieurs niveaux : seul, un
      // "niveau 1" n'apprendrait rien.
      ...(active && sortStack.length > 1 ? { detail: `niveau ${at + 1}` } : {}),
    };
  });

  /* ---- Les tags du tiroir (4a) ---- */

  const filterLabelOf = (id: string): string =>
    toolFilters.find((filter) => filter.id === id)?.label ?? id;
  const sortLabelOf = (key: string): string =>
    sortOptions.find((option) => option.key === key)?.label ?? key;
  // LE TAG DE MODE COMPTE COMME UN TAG : il ouvre le tiroir tout seul, comme le
  // premier filtre pose, et il fait apparaitre le mot qui vide tout.
  const hasTags = archivesMode || activeFilterIds.length > 0 || sortStack.length > 0;

  /*
   * LE LIBELLE DU BOUTON 4b, unique explication d'un controle sans texte : il
   * sert de `title` ET d'`aria-label` (G16, tout controle desactive porte sa
   * raison). DEUX raisons de desactivation, deux phrases : `canKeepFavorite`
   * seul ne les distingue pas, il est faux dans les deux cas. Elles se
   * DEDUISENT de ce que le moteur donne deja, aucun champ de plus a declarer :
   * "aucun ecart a l'etat de base" = aucun filtre actif ET pile de tri vide ;
   * "deja garde" = il y a un ecart et le moteur refuse quand meme.
   *
   * LE MODE ARCHIVES N'ENTRE PAS DANS CE CALCUL, et c'est pour ca qu'il ne lit
   * pas `hasTags` : un favori garde des filtres et une pile de tri, jamais un
   * mode. En mode archives sans aucun filtre, l'ecart a garder est donc bien
   * NUL.
   *
   * MAIS IL LUI FAUT SA PROPRE PHRASE, ET C'EST LA TROISIEME. "Rien a garder :
   * l'ecran est a son etat de base" se lisait a deux centimetres du tag orange
   * [Mode Archives] : le bouton NIAIT ce qui etait a l'ecran. La phrase du mode
   * enonce donc la REGLE au lieu de nier l'evidence, ce qui est aussi la seule
   * facon d'apprendre qu'un favori rappelle des filtres et un tri, pas un etat
   * de l'ecran.
   */
  const hasFilterOrSortTags = activeFilterIds.length > 0 || sortStack.length > 0;
  const keepLabel = canKeep
    ? 'Garder cet etat en favori'
    : hasFilterOrSortTags
      ? 'Cet etat est deja un favori'
      : archivesMode
        ? "Un mode ne se garde pas en favori : un favori ne retient que des filtres et un tri."
        : "Rien a garder : l'ecran est a son etat de base";

  /* ---- Rangee 3a : la regle "+ Ajouter" et le lime unique ---- */

  // L'action `kind: 'add'` est rendue en premier, en lime, libelle universel.
  // S'il n'y en a pas, la premiere action declaree `primary` garde sa
  // promotion (decision motivee d'ecran) ; s'il y en a une, toute autre
  // `primary` est RETROGRADEE : jamais deux limes sur un ecran.
  const addAction = quickActions.find((action) => action.kind === 'add');
  const otherActions =
    addAction == null ? quickActions : quickActions.filter((action) => action !== addAction);
  const promotedId =
    addAction == null
      ? quickActions.find((action) => action.variant === 'primary')?.id
      : undefined;
  const hasRowActions = quickActions.length > 0 || viewActions.length > 0;

  /*
   * "+ AJOUTER" SE DESACTIVE EN MODE ARCHIVES, AVEC SA RAISON, JAMAIS CACHE
   * (G16). On n'ajoute pas une fiche a un jeu de fiches RETIREES du travail
   * courant : elle en sortirait aussitot, ou n'y entrerait jamais. Il faut donc
   * qu'on sache que le bouton existe et a quelle condition il sert.
   *
   * C'est le seul moment ou l'ecran n'a plus AUCUN lime, et c'est assume.
   *
   * LA RAISON EST PORTEE PAR UNE COQUE, PAS PAR LE BOUTON. Le `Button` du socle
   * porte `disabled:pointer-events-none` : desactive, il ne recoit aucun
   * evenement de souris, donc son propre `title` ne s'afficherait jamais. Le
   * `title` est pose sur l'enveloppe, qui elle reste survolable, et le bouton
   * garde la raison en `aria-label` pour le clavier et les lecteurs d'ecran.
   */
  const ADD_BLOCKED_REASON = "On n'ajoute pas dans les archives. Quitte le mode pour ajouter.";
  const addBlocked = archivesMode;
  const addLabel = addBlocked ? ADD_BLOCKED_REASON : 'Ajouter';

  // Le mot qui vide tout (zone 4a) DIT sa portee reelle : depuis le chantier
  // 2_0, `clearAll()` eteint aussi le mode. Le texte visible ne bouge pas
  // ("Effacer filtres", deux poids dans la rangee) ; c'est la raison lue au
  // survol et par les lecteurs d'ecran qui suit le geste.
  const clearAllLabel = archivesMode
    ? 'Effacer filtres, tri et mode archives'
    : 'Effacer filtres et tri';

  // La rangee 3 se rend des qu'une de ses parties existe, et reste affichee
  // dans TOUS les etats de la zone 5 ET aux deux niveaux : la masquer ferait
  // de la fiche une nasse, sans selecteur ni puce de sortie (3c jamais
  // conditionnel). Les props de fiche (`onRecordExit`, `saveState`) n'entrent
  // pas dans ce calcul : un ecran sans actions, sans outils et sans vues ne
  // gagne aucune rangee.
  const hasCommandRow =
    hasRowActions || filters.length > 0 || tools != null || views.length > 0;

  // Boite commune des boutons de la rangee 3a : h 2rem, rayon sm, libelle
  // 0.8125rem. Le libelle ne se coupe jamais (G13 : plafond 2rem, title
  // complet) ; la rangee defile au-dela.
  // C'EST LA CONSTANTE DE LA BARRE DE LOT, PAS UNE COPIE (voir `BULK_BUTTON`) :
  // la rangee 3 change de nature sans changer de hauteur. Deux chaines jumelles
  // tenues par un commentaire finissent par diverger, et toute la zone 5
  // sauterait alors d'un cran au premier clic dans une case a cocher.
  const actionButtonClasses = BULK_BUTTON;

  // Chargement temporise (useLoadingState) : skeleton apres un court delai,
  // duree minimale d'affichage, timeout vers l'etat erreur standard.
  const { showLoading, timedOut } = useLoadingState(loading);

  const retryAction =
    onRetry != null ? (
      <Button variant="secondary" size="sm" onClick={onRetry}>
        {retryLabel}
      </Button>
    ) : undefined;

  // Etats standard de la zone 5, par priorite :
  // error > loading > empty > children.
  let zone5: ReactNode;
  if (error != null && error !== '') {
    zone5 = (
      <StateBlock
        role="alert"
        title={errorTitle ?? 'Something went wrong'}
        hint={error}
        action={retryAction}
      />
    );
  } else if (loading && timedOut) {
    // Chargement anormalement long : etat erreur standard, avec relance si
    // l'ecran fournit `onRetry`.
    zone5 = (
      <StateBlock
        role="alert"
        title={errorTitle ?? 'Still loading, something may be wrong'}
        action={retryAction}
      />
    );
  } else if (loading && showLoading) {
    // Skeleton (decoratif : aria-hidden ; l'etat est porte par aria-busy sur
    // la zone 5). Cle dediee : le swap vers le contenu remonte un nouveau
    // conteneur et rejoue le fondu.
    zone5 = (
      <div key="skeleton" aria-hidden className="taki-anim-fade">
        {skeleton ?? <SkeletonListRows />}
      </div>
    );
  } else if (empty && !loading) {
    // L'ETAT VIDE A SON PROPRE TEXTE EN MODE ARCHIVES. Le message standard de
    // l'ecran dirait qu'il n'y a rien du tout, ce qui est faux et inquietant :
    // ici, il n'y a rien D'ARCHIVE, et la sortie est nommee dans la phrase.
    //
    // CETTE BRANCHE NE SERT QU'AUX ECRANS QUI PASSENT ENCORE `empty` (les V1).
    // Un ecran a portes delegue sa zone 5 au `ViewHost`, qui tient la priorite
    // des etats et rend le MEME bloc : c'est LA que ce texte sort en pratique.
    // Les deux doivent rester jumeaux, toute retouche de l'un se reporte sur
    // l'autre.
    zone5 = archivesMode ? (
      <StateBlock
        title="Aucune fiche archivee"
        hint="Quitte le mode archives pour retrouver le travail courant."
      />
    ) : (
      <StateBlock title={emptyTitle} hint={emptyHint} />
    );
  } else {
    // Contenu reel (ou fenetre de delai avant skeleton : on garde le contenu
    // affiche, la donnee rapide arrive sans aucun flash). PAS de fondu ici :
    // le contenu monte a chaque changement d'app, un fondu depuis opacity 0 se
    // lirait comme un flash. Le contenu apparait d'un coup ; seul le skeleton
    // (chargement lent avere) fait un fondu d'entree. h-full : conserve le
    // comportement d'un contenu pose directement dans la zone qui defile.
    zone5 = (
      <div key="content" className="h-full">
        {children}
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full bg-surface font-sans text-ink-900">
      {/* ------------------------------------------------------------------
          Zone 1 : le rail. Identite (1a) + menu des portes (1b), RIEN d'autre :
          le rail s'arrete au menu. Les actions vivent en 3a, la zone 7 est
          supprimee.
          ------------------------------------------------------------------ */}
      <aside className="flex w-[13.5rem] shrink-0 flex-col border-r border-border-soft">
        <nav
          aria-label={appName != null ? `Menu ${appName}` : 'Menu'}
          className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-3 pb-3 pt-4"
        >
          {hasAppIdentity && (
            <div className="mb-3 flex h-9 items-center gap-2.5 px-2">
              {appIcon != null &&
                (onBack != null ? (
                  /* Le shell fournit `onBack` : la puce d'icone devient un
                     bouton retour. Au survol, l'icone d'app se change en fleche
                     back ; au clic, retour a la derniere app visitee ou au
                     home. `backLabel` sert de tooltip natif. */
                  <button
                    type="button"
                    onClick={onBack}
                    title={backLabel}
                    aria-label="Retour"
                    className={cn(
                      'group flex h-8 w-8 shrink-0 items-center justify-center rounded-[0.625rem]',
                      'bg-surface-3 text-ink-700 transition-colors',
                      'hover:bg-accent-tint hover:text-accent-ink',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
                    )}
                  >
                    <span className="flex items-center justify-center group-hover:hidden">
                      {appIcon}
                    </span>
                    <span className="hidden items-center justify-center group-hover:flex">
                      <IconChevronRight size={18} className="rotate-180" />
                    </span>
                  </button>
                ) : (
                  /* Puce d'icone de l'app : UNE par app, en tete du rail (1a),
                     toujours celle de l'app courante, jamais remplacee (G1). */
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[0.625rem] bg-surface-3 text-ink-700">
                    {appIcon}
                  </span>
                ))}
              <span className="min-w-0 truncate text-sm font-semibold text-ink-900">{appName}</span>
            </div>
          )}
          {menuItems.map((item) => (
            <RailItem
              key={item.id}
              icon={item.icon}
              label={item.label}
              count={item.count}
              active={item.id === activeMenuId}
              onClick={() => onMenuSelect?.(item.id)}
            />
          ))}
        </nav>
      </aside>

      {/* ------------------------------------------------------------------
          Colonne principale : en-tete (2), rangee de commandes (3), tiroir (4),
          contenu (5).
          ------------------------------------------------------------------ */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Zone 2 : titre (2a), recherche centree sur l'ecran (2b), stats (2c).
            L'ORDRE DE CE QUI CEDE est une regle de chassis : 2c ne cede jamais,
            la recherche cede jusqu'a 9rem, le titre tronque en dernier. Les
            colonnes sont posees sur le conteneur et chaque cellule est placee
            explicitement : sans stat comme sans recherche, la grille reste la
            meme et le titre ne bouge pas (l'equivalent React du [hidden] du
            proto). */}
        <header className="grid shrink-0 grid-cols-[minmax(0,1fr)_minmax(9rem,22rem)_minmax(0,1fr)] items-center gap-5 border-b border-border-soft px-5 py-2">
          <h1 className="col-start-1 min-w-0 truncate text-[1.3125rem] font-bold tracking-[-0.02em] text-ink-900">
            {headerTitle}
          </h1>
          {hasSearch && (
            /* 2b : h 2rem, rayon sm, icone ETINCELLE (la promesse "demander a
               Lia" appartient au shell : ici, un placeholder par porte, rien
               de cable). Rendue aux deux niveaux : la requete survit a la
               fiche et revient telle quelle en collection.

               PENDANT UN LOT, ELLE EST HORS SERVICE, exactement comme le
               tiroir : hachuree, inerte, et elle DIT pourquoi. C'etait le seul
               geste de l'ecran qui detruisait du travail SANS UN MOT, et le
               paradoxe etait net : la rangee 3 a ete videe et le tiroir hachure
               precisement pour empecher qu'un filtre change le jeu sous un lot
               en cours, pendant que la recherche, elle, faisait la meme chose EN
               PIRE. Elle vide la selection ENTIERE des le premier caractere,
               avant meme d'avoir filtre quoi que ce soit : vingt et un clics
               perdus par le reflexe le plus naturel du monde, cliquer dans la
               recherche pour trouver une vingt-deuxieme ligne a ajouter.
               Chercher fait de toute facon tomber le lot ; mieux vaut REFUSER
               le geste que le laisser detruire en silence.

               LA RAISON EST PORTEE PAR LA COQUE, PAS PAR LE CHAMP : le champ est
               `inert`, il ne recoit donc aucun evenement de souris et son propre
               `title` ne s'afficherait jamais. Meme procede que les boutons
               eteints de la barre de lot. */
            <div
              title={bulkActive ? SEARCH_OFF_REASON : undefined}
              className={cn(
                'col-start-2 flex h-8 w-full min-w-0 items-center rounded-sm border border-border bg-surface-1',
                'transition-colors',
                bulkActive
                  ? // Le survol n'allume plus le contour : un champ hors service
                    // qui repond au survol se relit comme un champ qui marche.
                    cn('cursor-not-allowed', DRAWER_HATCH)
                  : cn(
                      'hover:border-border-strong',
                      'focus-within:border-accent-border focus-within:shadow-ring-soft',
                    ),
              )}
            >
              <span
                aria-hidden
                className="pointer-events-none flex shrink-0 items-center pl-3 text-ink-300"
              >
                <IconSearch size={15} />
              </span>
              <input
                type="text"
                // HORS SERVICE PENDANT UN LOT, PAR LE MEME IDIOME QUE LE
                // TIROIR : `inert`, jamais `disabled`. Un champ `disabled` se
                // grise par les styles du navigateur PAR-DESSUS la hachure, et
                // les deux zones eteintes de l'ecran s'eteindraient alors de
                // deux facons differentes pour un seul et meme etat. `inert`
                // retire le champ du clavier et de la souris, la hachure dit le
                // reste, et la coque porte la raison.
                inert={bulkActive}
                value={searchFieldValue}
                onChange={(event) => {
                  if (!searchControlled) setInternalSearch(event.target.value);
                  onSearchChange(event.target.value);
                }}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                className={cn(
                  'h-full w-full min-w-0 bg-transparent pl-2 pr-3.5 font-sans text-[0.8125rem] text-ink-900',
                  'placeholder:text-ink-300 focus-visible:outline-none',
                )}
              />
            </div>
          )}
          {showStats && (
            /* 2c : memes boites que SkeletonStats (calque au rem pres) : toute
               retouche ici se reporte la-bas, sinon le squelette saute. */
            <div className="col-start-3 flex shrink-0 items-stretch gap-4 justify-self-end">
              {stats.map((stat, index) => (
                <Fragment key={`${stat.label}-${index}`}>
                  {index > 0 && <span aria-hidden className="w-px bg-border-soft" />}
                  <div className="text-right">
                    <span className="block text-base font-semibold leading-tight text-ink-900">
                      {stat.value}
                    </span>
                    <span className="block text-[0.6875rem] text-ink-400">{stat.label}</span>
                  </div>
                </Fragment>
              ))}
            </div>
          )}
        </header>

        {/* RANGEE 3, TROISIEME ETAT : LA BARRE DE LOT. Elle REMPLACE la rangee
            entiere (3a, 3b et 3c) des qu'une ligne est cochee.

            C'EST UN BLOC FRERE, PAS UNE BRANCHE DANS LA RANGEE NORMALE, et
            c'est volontaire : la rangee normale garde ainsi son balisage EXACT
            (memes conditions, meme imbrication, meme indentation), donc le
            troisieme etat n'a rien pu casser des deux premiers. Les deux
            portent la MEME coque (`relative z-10`, memes filets, meme retrait,
            meme hauteur de boutons) : la rangee change de nature sans que rien
            ne saute a l'ecran.

            `hasCommandRow` N'ENTRE PAS DANS CETTE CONDITION : une selection
            n'arrive que par `tools`, donc `hasCommandRow` est vrai de toute
            facon ; et si un jour ce n'etait plus le cas, un lot sans barre
            serait un lot sans sortie. */}
        {bulkActive && (
          <div className="relative z-10 flex shrink-0 items-center gap-2 border-b border-border-soft px-5 py-2">
            <BulkBar
              count={selection.length}
              availability={bulkBarAvailability}
              {...(tools?.clearSelection != null
                ? { onClear: () => tools.clearSelection?.() }
                : {})}
              onEdit={() => {
                // UN LOT A LA FOIS (voir `bulkBusy`), la meme ceinture que dans
                // `askBulk`. Le bouton est deja eteint avec sa raison, et un
                // bouton eteint sort de l'ordre de tabulation : le `Maj + Tab`
                // depuis une fenetre ouverte ne tombe donc plus dessus. Cette
                // ligne reste parce qu'une garde qui depend d'un rendu n'est pas
                // une garde.
                if (bulkBusy) return;
                // LE LOT EST GELE ICI AUSSI, noms compris : la fenetre travaille
                // sur les ids qu'on vient de voir, pas sur ceux qu'elle relirait
                // plus tard.
                setBulkEdit({ ids: [...selection], labelOf: freezeLabels() });
                setBulkEditOpen(true);
              }}
              onArchive={() => {
                if (archiveOne != null) askBulk('archive', archiveOne);
              }}
              onRemove={() => {
                if (removeOne != null) askBulk('remove', removeOne);
              }}
            />
          </div>
        )}
        {/* Rangee 3 : 3a actions a gauche, vide au milieu, 3b outils puis le
            groupe epingle 3c a droite. `relative z-10` : les popovers des
            outils se posent PAR-DESSUS la zone 5, jamais rognes par elle.
            Au niveau fiche, la partie gauche devient puce de sortie +
            indicateur d'enregistrement et le groupe epingle passe du selecteur
            de vues au curseur de fiche ; il garde son x (le groupe de gauche
            garde flex-1 aux deux niveaux). */}
        {hasCommandRow && !bulkActive && (
          <div className="relative z-10 flex shrink-0 items-center gap-2 border-b border-border-soft px-5 py-2">
            {isRecordLevel ? (
              <div className="flex min-w-0 flex-1 items-center gap-2.5">
                {onRecordExit != null && (
                  /* La puce de SORTIE de fiche : h 2rem, la ligne de base de
                     toute la rangee. La sortie au doigt et la sortie au
                     clavier sont le MEME geste, mais la touche n'est PAS
                     ANNONCEE (decision du fondateur du 07/08/2026) : Echap sert
                     deja a fermer un editeur et a annuler une saisie sur cet
                     ecran, l'afficher promettait une touche a sens unique. Le
                     geste reste entierement cable (`useScreen`), il n'est plus
                     montre NULLE PART, `title` compris : une infobulle qui
                     souffle « Echap » une seconde apres le survol refait
                     exactement la promesse qu'on vient de retirer, et elle la
                     refait a la personne qui hesite. Le `title` nomme donc la
                     DESTINATION, comme dans le prototype ("Retour a la Liste").
                     La puce GARDE sa coque la ou le groupe epingle est nu :
                     elle porte un MOT et non un glyphe, et un mot pose au bord
                     d'une rangee sans coque se lit comme un titre, pas comme un
                     bouton. */
                  <button
                    type="button"
                    onClick={onRecordExit}
                    title={`Retour a ${recordExitLabel}`}
                    aria-label={`Fermer la fiche et revenir a ${recordExitLabel}`}
                    className={cn(
                      'flex h-8 shrink-0 items-center gap-2 rounded-[0.625rem] border border-border bg-surface px-3',
                      'font-sans text-xs font-semibold text-ink-700 transition-colors',
                      'hover:bg-surface-1 hover:text-ink-900',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
                    )}
                  >
                    {/* Le chevron du fondateur (`left_back.svg`), pas un
                        Streamline : c'est le glyphe que le prototype REND ici
                        (`inventaire-fiche.html`, viewBox 131.04 x 226.83 a
                        0.75rem), et la puce etant desormais le SEUL signal
                        visible de la sortie, elle ne peut pas garder le plus
                        maigre des deux dessins. Meme taille que le prototype :
                        12 px de boite, soit 0.75rem. */}
                    <IconLeftBack size={12} />
                    <span className="truncate">{recordExitLabel}</span>
                  </button>
                )}
                {saveState != null && (
                  <SaveIndicator
                    state={saveState}
                    {...(onRetrySave != null ? { onRetry: onRetrySave } : {})}
                  />
                )}
              </div>
            ) : (
              /* 3a + pastilles V1 : le groupe DEFILANT. Le survol du primaire
                 est SOBRE (fond eclairci, rien ne bouge, aucune ombre, choix
                 fondateur du 04/08) : aucun degagement d'ombre a menager. Si un
                 relief revenait un jour, se souvenir qu'un conteneur overflow
                 tranche tout ce qui depasse (halo compris) : c'est ce qui a
                 tue l'ancien survol. */
              <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto">
                {addAction != null && (
                  /* La COQUE porte le title : desactive, le bouton ne recoit
                     plus la souris et n'afficherait jamais le sien (voir
                     `ADD_BLOCKED_REASON` plus haut). Elle est en `flex` et
                     `shrink-0` pour ne rien changer a la geometrie de la
                     rangee : la coque a exactement la boite du bouton. */
                  <span key={addAction.id} title={addLabel} className="flex shrink-0">
                    <Button
                      variant="primary"
                      size="sm"
                      className={actionButtonClasses}
                      aria-label={addLabel}
                      onClick={addAction.onClick}
                      disabled={addAction.disabled === true || addBlocked}
                    >
                      <IconPlus size={15} className="shrink-0" />
                      Ajouter
                    </Button>
                  </span>
                )}
                {otherActions.map((action) => (
                  <Button
                    key={action.id}
                    variant={action.id === promotedId ? 'primary' : 'secondary'}
                    size="sm"
                    className={actionButtonClasses}
                    title={action.label}
                    onClick={action.onClick}
                    disabled={action.disabled}
                  >
                    {action.icon != null && (
                      <span className="flex shrink-0 items-center justify-center">
                        {action.icon}
                      </span>
                    )}
                    <span className="whitespace-nowrap">{action.label}</span>
                  </Button>
                ))}
                {/* Les actions de la vue : TOUJOURS secondaires, quoi
                    qu'elles declarent. Clef prefixee : une vue et un ecran
                    peuvent porter le meme id d'action sans se marcher
                    dessus. */}
                {viewActions.map((action) => (
                  <Button
                    key={`view:${action.id}`}
                    variant="secondary"
                    size="sm"
                    className={actionButtonClasses}
                    title={action.label}
                    onClick={action.onClick}
                    disabled={action.disabled}
                  >
                    {action.icon != null && (
                      <span className="flex shrink-0 items-center justify-center">
                        {action.icon}
                      </span>
                    )}
                    <span className="whitespace-nowrap">{action.label}</span>
                  </Button>
                ))}
                {/* LECTURE V1 : les pastilles a choix unique, dans la meme
                    rangee (l'ancienne zone 4 a fusionne ici). Elles vivent
                    jusqu'a la migration de l'ecran vers `tools`. */}
                {filters.map((filter) => {
                  const active = filter.id === activeFilterId;
                  return (
                    <button
                      key={filter.id}
                      type="button"
                      onClick={
                        onFilterSelect != null ? () => onFilterSelect(filter.id) : undefined
                      }
                      aria-pressed={active}
                      className={cn(
                        'h-8 shrink-0 rounded-pill border px-3.5 font-sans text-xs font-semibold transition-colors',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
                        active
                          ? 'border-ink-900 bg-ink-900 text-surface'
                          : 'border-border bg-surface text-ink-500 hover:bg-surface-1 hover:text-ink-900',
                      )}
                    >
                      {filter.label}
                    </button>
                  );
                })}
              </div>
            )}
            {/* 3b : le groupe d'outils, meme shell que le selecteur de vues.
                Porte sans tranches (`sections: false`) : il ne garde que le
                tri. Absent au niveau fiche (la fiche ne se filtre pas). */}
            {!isRecordLevel && hasToolGroup && (
              <div
                ref={toolsHostRef}
                role="group"
                aria-label="Outils"
                className="flex h-8 shrink-0 items-center gap-0.5 rounded-md border border-border bg-surface-1 px-0.5"
              >
                {showFilterTool && (
                  <span className="relative flex">
                    <ToolButton
                      icon={<IconFilter2 size={15} />}
                      label="Filtrer"
                      /* LE POINT S'ALLUME AUSSI POUR LE MODE : popover replie,
                         il faut qu'on sache qu'on est dedans. Il reste en LIME
                         (`--accent-dot`), couleur heritee du chantier 1_2 :
                         voir l'entete de `ToolButton`, elle ne se rediscute
                         pas ici. */
                      marked={activeFilterIds.length > 0 || archivesMode}
                      expanded={openPop === 'filter'}
                      controls={filterPopId}
                      haspopup
                      onClick={() => setOpenPop((open) => (open === 'filter' ? null : 'filter'))}
                    />
                    {openPop === 'filter' && (
                      <FilterPopover
                        id={filterPopId}
                        items={filterItems}
                        onPick={(id) => tools?.toggleFilter?.(id)}
                        showCatalogue={showFilterCatalogue}
                        clearDisabled={activeFilterIds.length === 0}
                        onClear={() => tools?.clearFilters?.()}
                        archivesMode={archivesMode}
                        {...(tools?.setArchivesMode != null
                          ? {
                              onArchivesToggle: (on: boolean) => {
                                /* ENTRER passe par la fenetre, SORTIR non. */
                                if (!on) {
                                  tools.setArchivesMode?.(false);
                                  return;
                                }
                                setOpenPop(null);
                                setAskArchivesMode(true);
                              },
                            }
                          : {})}
                      />
                    )}
                  </span>
                )}
                {showSortTool && (
                  <span className="relative flex">
                    <ToolButton
                      /* Le glyphe de tri : une icone du set, PIVOTEE (deux
                         fleches opposees a la verticale), jamais un dessin
                         maison. Meme procede que la fleche de retour. */
                      icon={<IconLineArrowTransferHorizontal1 size={15} className="rotate-90" />}
                      label="Trier"
                      marked={sortStack.length > 0}
                      expanded={openPop === 'sort'}
                      controls={sortPopId}
                      haspopup
                      onClick={() => setOpenPop((open) => (open === 'sort' ? null : 'sort'))}
                    />
                    {openPop === 'sort' && (
                      <ToolPopover
                        id={sortPopId}
                        label="Trier"
                        items={sortItems}
                        onPick={(key) => tools?.toggleSort?.(key)}
                        clearLabel="Ordre d'origine"
                        clearDisabled={sortStack.length === 0}
                        onClear={() => tools?.clearSort?.()}
                      />
                    )}
                  </span>
                )}
                {showStarTool && (
                  /* L'etoile commande le TIROIR (jamais fermee par Escape ni
                     par un clic dehors : seul son bouton, ou une porte).
                     L'etoile SIMPLE en contour (`fav.svg`) : elle ouvre la
                     barre, elle ne fabrique rien. Celle du tiroir (4b) porte le
                     PLUS, sinon les deux boutons sont indiscernables. */
                  <ToolButton
                    icon={<IconFav size={15} />}
                    label="Favoris"
                    expanded={drawerOpen}
                    controls={drawerId}
                    onClick={() => {
                      setOpenPop(null);
                      tools?.setDrawerOpen?.(!drawerOpen);
                    }}
                  />
                )}
              </div>
            )}
            {/* 3c : LE GROUPE EPINGLE, toujours le dernier de la rangee. Sa
                place ne change JAMAIS, son CONTENU change avec le niveau : au
                niveau collection le selecteur de vues, au niveau fiche le
                curseur puis les gestes sur la fiche. C'est ce qui fait que la
                rangee ne saute pas quand on ouvre une ligne. Aucun filet avant
                lui : deux groupes cote a cote se separent deja tout seuls. */}
            {isRecordLevel ? (
              recordCursor != null && (
                <RecordNavGroup
                  cursor={recordCursor}
                  actions={menuActions}
                  onRunAction={runRecordAction}
                  {...(onRecordNavigate != null ? { onNavigate: onRecordNavigate } : {})}
                />
              )
            ) : (
              <ViewSelector
                views={collectionViews}
                {...(activeViewId != null ? { activeViewId } : {})}
                {...(onViewSelect != null ? { onViewSelect } : {})}
              />
            )}
          </div>
        )}

        {/* Zone 4 : le TIROIR (tags 4a, garder 4b, favoris 4c). Replie par
            defaut (max-height 0), il s'ouvre au premier tag (regle du moteur)
            et a la demande par l'etoile. Jamais rendu au niveau fiche : l'etat
            est garde par le moteur et revient au retour en collection. La
            barre est a peine teintee (--surface-1), a plat : ce n'est pas une
            Card. `inert` quand il est replie : rien de repliable n'est
            atteignable au clavier. */}
        {/* PENDANT UNE SELECTION, LE TIROIR NE DISPARAIT PAS : IL SE HACHURE.
            Le faire disparaitre ferait sauter la rangee entiere et ferait
            oublier l'etat DEPUIS LEQUEL la selection a ete faite. Hachure, il
            reste lisible et dit qu'il est HORS SERVICE tant que dure le lot :
            filtrer pendant qu'on agit sur un lot serait de toute facon un geste
            qui se contredit (le filtre ferait sortir des lignes du jeu, donc de
            la selection).
            SON ETAT EST GARDE ET REVIENT EXACTEMENT COMME IL ETAIT, sans une
            ligne de code pour ca : rien n'est demonte, rien n'est remis a zero.
            `drawerOpen` appartient au moteur et le chassis n'y touche pas.
            `inert` S'AJOUTE A LA HACHURE. Le calque bloque la SOURIS ; sans
            `inert`, la tabulation garderait une porte derobee vers des
            controles que le doigt ne peut plus atteindre, et "Effacer filtres"
            au clavier changerait le jeu sous un lot en cours. C'est deja
            l'idiome du tiroir replie, quelques caracteres plus loin.
            MAIS `inert` DESCEND D'UN CRAN, SUR LA GRILLE INTERIEURE, et la
            coque exterieure reste survolable : c'est elle qui porte la RAISON
            (`title`) et le curseur barre. Pose sur la coque, `inert` la rendait
            muette : le calque bloque la souris, `inert` bloque le reste, donc
            aucun `title` de l'interieur n'aurait jamais pu s'afficher, et cinq
            clics sans effet (un tag, sa croix, "Effacer filtres", un favori) ne
            s'expliquaient nulle part. La grille etant le seul enfant de la
            coque, ce que `inert` couvre n'a pas change d'un element. */}
        {showDrawer && (
          <div
            id={drawerId}
            title={bulkActive ? DRAWER_OFF_REASON : undefined}
            className={cn(
              'shrink-0 overflow-hidden transition-[max-height]',
              drawerDeployed ? 'max-h-24' : 'max-h-0',
              bulkActive && 'cursor-not-allowed',
            )}
            style={{
              transitionDuration: 'var(--dur-base)',
              transitionTimingFunction: 'var(--ease-standard)',
            }}
          >
            {/* TROIS TIERS, colonnes FIXES : deux tiers pour l'ETAT COURANT
                (les tags defilants, puis le mot qui vide tout), un filet
                STRUCTUREL, un tiers pour les RACCOURCIS (le bouton Garder puis
                les favoris, alignes a GAUCHE dans leur tiers).
                Correction fondateur du 04/08 apres essai : quand les favoris
                partageaient la ligne avec les tags, chaque bascule de favori
                changeait la longueur des tags et faisait sauter les pastilles
                qu'on venait de cliquer. La colonne de droite ne depend donc NI
                des tags NI de ce que la gauche porte.
                Le passage aux trois tiers (07/08) etend cette raison au tiers
                de droite lui-meme : le bouton Garder y est TOUJOURS rendu,
                seulement desactive quand il n'y a rien a garder. Un bouton qui
                apparait et disparait ferait sauter les pastilles pour
                exactement la meme raison. */}
            {/* PENDANT UN LOT, LE TIROIR SE HACHURE (voir `DRAWER_HATCH`), et
                c'est ICI que vit `inert` : replie (rien de repliable n'est
                atteignable au clavier) comme pendant un lot. La coque
                exterieure, elle, reste survolable pour porter la raison. */}
            <div
              inert={!drawerDeployed || bulkActive}
              className={cn(
                'grid grid-cols-[minmax(0,2fr)_auto_minmax(0,1fr)] items-center gap-2.5 border-b border-border bg-surface-1 px-5 py-[0.4375rem]',
                bulkActive && DRAWER_HATCH,
              )}
            >
              {/* LES DEUX TIERS DE GAUCHE : l'ETAT COURANT. L'ecart de gap est
                  porteur de sens : 0.625rem entre les tags et le mot qui vide
                  tout, 0.375rem entre tags. C'est ce qui empeche le mot de se
                  lire comme un tag de plus. */}
              <div className="flex min-w-0 items-center gap-2.5">
                {/* 4a : les tags, dimensionnes par leur contenu, defilants.
                    Ils disent l'ECART a l'etat de base : rien a la base, l'etat
                    complet des qu'il y a un ecart. PILE DE TRI VIDE = l'ordre
                    du chargeur, et on n'affiche RIEN : le pseudo-tag "Ordre
                    d'origine" qui le disait etait du bruit, il a ete retire
                    (decision du fondateur du 07/08). L'entree "Ordre d'origine"
                    du popover de TRI, elle, reste : c'est le chemin qui vide la
                    pile. */}
                {hasTags && (
                  <div className="flex min-w-0 flex-[0_1_auto] items-center gap-1.5 overflow-x-auto">
                    {/* LE TAG DE MODE, EN TETE, AVANT LES FILTRES. Meme boite,
                        meme croix, meme retrait qu'un tag de filtre : c'est un
                        objet de l'etat courant comme les autres. Deux choses
                        changent, et deux seulement : son GENRE dit "Mode" et
                        non "Filtre", et son contour passe a l'orange
                        (`--mode-border`). Un mode ne tranche pas la collection,
                        il la change entierement, et aucune couleur du systeme
                        ne disait ca.
                        Son corps est INERTE (rien a inverser), sa croix est la
                        sortie : sortir ne redemande RIEN. */}
                    {archivesMode && (
                      <DrawerTag
                        key="mode:archives"
                        kind="Mode"
                        value="Archives"
                        tone="mode"
                        removeLabel="Quitter le mode archives"
                        {...(tools?.setArchivesMode != null
                          ? { onRemove: () => tools.setArchivesMode?.(false) }
                          : {})}
                      />
                    )}
                    {/* LES TAGS DE FILTRE S'ETEIGNENT EN MODE ARCHIVES. Sans
                        ca, le tiroir affirme qu'un filtre s'applique (tag
                        plein, contour lime, croix active) a deux centimetres
                        d'un popover qui grise ses entrees et ecrit dessous que
                        les filtres ne s'appliquent pas. Deux objets du meme
                        ecran disaient le contraire l'un de l'autre.
                        Ils ne DISPARAISSENT pas : ils sont toujours poses, ils
                        reviendront a la sortie du mode (le moteur les garde
                        volontairement), et un tag qui s'evapore laisserait
                        croire qu'ils ont ete vides. */}
                    {activeFilterIds.map((id) => (
                      <DrawerTag
                        key={`filter:${id}`}
                        kind="Filtre"
                        value={filterLabelOf(id)}
                        off={archivesMode}
                        offReason={`${filterLabelOf(id)} : ${ARCHIVES_FILTER_OFF}`}
                        onRemove={() => (tools?.removeFilter ?? tools?.toggleFilter)?.(id)}
                      />
                    ))}
                    {/* LES TAGS DE TRI, EUX, NE S'ETEIGNENT JAMAIS : le tri
                        continue de s'appliquer dans les archives, et choisir
                        une cle y reordonne bien la liste. */}
                    {sortStack.map((level, index) => (
                      <DrawerTag
                        key={`sort:${level.key}`}
                        kind="Tri"
                        value={sortLabelOf(level.key)}
                        dir={level.dir}
                        {...(sortStack.length > 1 ? { rank: index + 1 } : {})}
                        {...(tools?.invertSort != null
                          ? { onInvert: () => tools.invertSort?.(level.key) }
                          : {})}
                        {...(tools?.removeSort != null
                          ? { onRemove: () => tools.removeSort?.(level.key) }
                          : {})}
                      />
                    ))}
                  </div>
                )}
                {/* LE CONTROLE QUI VIDE TOUT : filtres ET pile de tri d'un
                    seul geste. UN MOT, PAS UNE CROIX : une croix nue posee
                    juste apres la croix du dernier tag donnait deux croix
                    identiques cote a cote, on lisait deux fois le meme geste
                    et la plus a droite semblait appartenir au tag qui la
                    precede. Un mot ne se confond avec rien, et il dit sa
                    PORTEE.
                    IL EST SOEUR DU CONTENEUR DEFILANT, PAS FILLE : dedans, il
                    sortirait de l'ecran des que la liste de tags deborde, et
                    un controle hors d'atteinte n'est plus un controle. Quand
                    les tags tiennent il suit visuellement le dernier ; quand
                    ils debordent il reste epingle en bout des deux tiers.
                    Peau NUE et graisse 500 (les tags gardent le 600 : la
                    rangee reste a deux poids, G17). Il NE REFERME PAS le
                    tiroir : seule l'etoile de 3b le commande, un tiroir qui se
                    derobe apres un clic serait une deuxieme reponse a un seul
                    geste.
                    IL NE SE REND QU'AVEC UN FILTRE OU UN TRI A VIDER, et non
                    des qu'il y a un tag. "Effacer filtres" est le seul mot
                    VISIBLE de ce controle : en mode archives sans aucun filtre
                    pose, il nommait une chose absente et faisait une chose
                    qu'il ne nomme pas (il eteignait le mode). La croix du tag
                    de mode porte deja cette sortie-la, et elle la nomme.
                    ECART CONNU, arbitrage du fondateur en attente, ANTERIEUR au
                    chantier 2_0 : le texte visible ne dit que "filtres" alors
                    que le geste emporte aussi le tri, et le mode quand il est
                    allume ; la portee complete est portee par le title et
                    l'aria-label, qui GARDENT le texte visible en prefixe (WCAG
                    "Label in Name"). "Tout effacer" veut dire tout : c'est le
                    moteur qui eteint le mode dans le meme `clearAll()`, en un
                    seul rendu. */}
                {hasFilterOrSortTags && tools?.clearAll != null && (
                  <button
                    type="button"
                    onClick={() => tools.clearAll?.()}
                    title={clearAllLabel}
                    aria-label={clearAllLabel}
                    className={cn(
                      'flex h-8 shrink-0 cursor-pointer items-center whitespace-nowrap rounded-pill border-none bg-transparent px-2.5',
                      'font-sans text-xs font-medium text-ink-500 transition-colors hover:bg-surface-3 hover:text-ink-900',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
                    )}
                  >
                    Effacer filtres
                  </button>
                )}
              </div>
              {showShortcuts && (
                <>
                  {/* LE FILET DES TIERS. Il ne separe plus des tags d'un bouton
                      (l'ancien filet, qui disparaissait avec les tags) : il
                      marque la frontiere entre L'ETAT COURANT et les
                      RACCOURCIS. Il est donc STRUCTUREL, et toujours rendu tant
                      que le tiers de droite existe, meme tiroir vide. Sur une
                      porte SANS TRANCHES il part avec lui : un filet qui ne
                      separe plus rien est un trait perdu. */}
                  <span aria-hidden className="h-6 w-px shrink-0 bg-border-strong" />
                  {/* LE TIERS DE DROITE : les RACCOURCIS, alignes a GAUCHE dans
                      leur tiers (pas a droite, pas centres). Le bouton en tete,
                      les favoris ensuite : c'est le bloc des favoris qui cede et
                      defile quand le tiers manque de place, jamais le bouton. */}
                  <div className="flex min-w-0 items-center justify-start gap-2.5">
                    {/* 4b : GARDER l'etat courant en favori. ICONE SEULE, carre
                        de 2rem, la taille des boutons d'outil de la rangee 3 :
                        le mot "Garder" ne parlait a personne.
                        PEAU NUE, ni contour ni fond, seulement un survol. Les
                        pilules bordees appartiennent aux tags et aux favoris,
                        qui sont des CHOSES ; effacer et garder sont des GESTES,
                        et les deux gestes du tiroir se ressemblent. Aucun lime.
                        Il ne se cache jamais A L'INTERIEUR de son tiers (voir le
                        commentaire des trois tiers) et porte sa raison en title
                        dans les DEUX etats (G16), avec l'aria-label qui va avec
                        puisqu'il n'a plus de texte.
                        IL S'ETEINT PAR `aria-disabled`, JAMAIS PAR `disabled` :
                        c'est la condition meme pour que sa raison soit
                        atteignable. Un bouton `disabled` ne recoit aucun
                        evenement de souris, donc son `title` ne s'affiche
                        jamais, et il sort de l'ordre de tabulation, donc son
                        `aria-label` n'est plus annonce non plus. Or l'etat le
                        plus courant du tiroir est justement l'ecran a sa base,
                        celui ou ce bouton est eteint : en `disabled` il serait
                        un rond gris sans texte, sans infobulle et sans focus,
                        exactement ce que G16 interdit.
                        Meme procede que le partage de la rangee 3c et que les
                        entrees grisees du selecteur de vues ; le geste, lui, est
                        garde ici, et le moteur se garde de son cote
                        (`canKeepFavorite`). */}
                    {tools?.keepFavorite != null && (
                      <button
                        type="button"
                        onClick={canKeep ? () => tools.keepFavorite?.() : undefined}
                        aria-disabled={!canKeep || undefined}
                        title={keepLabel}
                        aria-label={keepLabel}
                        className={cn(
                          'flex h-8 w-8 shrink-0 items-center justify-center rounded-pill border-none bg-transparent p-0',
                          'transition-colors',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
                          canKeep
                            ? 'cursor-pointer text-ink-500 hover:bg-surface-3 hover:text-ink-900'
                            : 'cursor-default text-ink-300',
                        )}
                      >
                        {/* L'ETOILE A PLUS du fondateur (`fav-ad-bigbig.svg`),
                            d'un seul tenant : le plus dit le geste (FABRIQUER un
                            favori) que le libelle ne dit plus depuis que le mot
                            "Garder" a disparu. La rangee 3b garde l'etoile
                            simple du meme set, elle OUVRE le tiroir. Deux
                            gestes, deux dessins. */}
                        <IconFavAddBigBig size={15} />
                      </button>
                    )}
                    {/* 4c : les favoris, a la suite du bouton Garder dans le
                        tiers de droite : leur position ne depend NI des tags NI
                        du bouton Garder, qui ne se cache jamais (ils ne bougent
                        pas quand on bascule de l'un a l'autre). Ce sont eux qui
                        cedent et defilent quand le tiers manque de place.
                        Actif = aplat encre (le meme vocabulaire que la pastille
                        active V1) : un favori actif et un tag ne se confondent
                        pas.
                        LE DEFILEMENT EST RAMENE EN BOUT quand la liste GRANDIT
                        (voir `favoritesScrollRef` plus haut) : la pastille qu'on
                        vient de fabriquer nait a la FIN, et sur une porte qui
                        declare deja plusieurs presets elle naitrait hors du
                        cadre, donc le geste de 4b n'aurait aucun retour a
                        l'ecran. */}
                    <div
                      ref={favoritesScrollRef}
                      className="flex min-w-0 flex-[0_1_auto] items-center justify-start gap-1.5 overflow-x-auto"
                    >
                      {favorites.map((favorite) => {
                        const active = favorite.id === (tools?.activeFavoriteId ?? null);
                        /* Seuls les favoris GARDES par l'utilisateur (`user-*`)
                           se retirent : les presets appartiennent a la porte. La
                           croix (meme anatomie que celle des tags 4a) n'apparait
                           qu'au survol de la pastille, et au focus clavier. */
                        const removable =
                          tools?.forgetFavorite != null && favorite.id.startsWith('user-');
                        /*
                         * EN MODE ARCHIVES, LES PASTILLES S'ETEIGNENT.
                         *
                         * Un favori RAPPELLE des filtres et une pile de tri ;
                         * dans les archives, les filtres ne s'appliquent pas. La
                         * pastille repondait quand meme : elle s'allumait en
                         * aplat encre, posait ses tags, et la liste ne perdait
                         * pas une ligne. Un menu qui repond alors qu'il ne
                         * s'applique pas est pire qu'un menu absent, parce qu'il
                         * fait douter de ce qu'on voit plutot que d'expliquer.
                         *
                         * MEME PROCEDE QUE LES ENTREES DU POPOVER, et meme
                         * raison en `title` : inertes, annoncees, jamais
                         * `disabled` (voir l'entete de `FilterPopover`).
                         */
                        const off = archivesMode;
                        const offLabel = `${favorite.label} : ${ARCHIVES_FILTER_OFF}`;
                        return (
                          <span
                            key={favorite.id}
                            {...(off ? { title: offLabel } : {})}
                            className={cn(
                              'group/fav flex h-8 shrink-0 items-stretch overflow-hidden rounded-pill border font-sans text-xs font-semibold transition-colors',
                              active
                                ? 'border-ink-900 bg-ink-900 text-surface'
                                : 'border-border bg-surface text-ink-500',
                              // Le survol part avec l'extinction : une pastille
                              // qui s'eclaire sous le doigt promet un clic.
                              !active && !off && 'hover:bg-surface-3 hover:text-ink-900',
                              off && 'opacity-35',
                            )}
                          >
                            <button
                              type="button"
                              aria-pressed={active}
                              aria-disabled={off || undefined}
                              tabIndex={off ? -1 : undefined}
                              onClick={off ? undefined : () => tools?.applyFavorite?.(favorite.id)}
                              {...(off ? { 'aria-label': offLabel } : {})}
                              className={cn(
                                'flex items-center border-none bg-transparent text-inherit',
                                removable ? 'pl-3.5 pr-1' : 'px-3.5',
                                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-border',
                                off ? 'pointer-events-none cursor-default' : 'cursor-pointer',
                              )}
                            >
                              {favorite.label}
                            </button>
                            {removable && (
                              <button
                                type="button"
                                aria-disabled={off || undefined}
                                tabIndex={off ? -1 : undefined}
                                onClick={off ? undefined : () => tools?.forgetFavorite?.(favorite.id)}
                                {...(off ? {} : { title: 'Retirer ce favori' })}
                                aria-label={`Retirer le favori ${favorite.label}`}
                                className={cn(
                                  'flex w-6 shrink-0 items-center justify-center border-none bg-transparent transition-colors',
                                  'opacity-0',
                                  // Eteinte, la croix ne se revele plus : une
                                  // croix qui parait sous le doigt sans rien
                                  // retirer invite au clic qu'elle refuse.
                                  !off && 'cursor-pointer focus-visible:opacity-100 group-hover/fav:opacity-100',
                                  off && 'pointer-events-none cursor-default',
                                  active
                                    ? 'text-surface hover:bg-ink-700'
                                    : 'text-ink-400 hover:bg-accent-tint hover:text-accent-ink',
                                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-border',
                                )}
                              >
                                <IconClose size={10} />
                              </button>
                            )}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Zone 5 : contenu, seule zone de contenu qui defile. `scrollRef` est
            PRETEE a l'ecran (donc au ViewHost et aux vues) : memoire de
            defilement et mesure de cadre. */}
        <section ref={scrollRef} aria-busy={loading} className="min-h-0 flex-1 overflow-y-auto">
          {zone5}
        </section>
      </div>

      {/* ------------------------------------------------------------------
          LES FENETRES DU CHASSIS, A LA RACINE de l'arbre et JAMAIS dans la
          rangee 3 : celle-ci est `relative z-10`, donc elle ouvre un contexte
          d'empilement dont un overlay ne sortirait plus.
          ------------------------------------------------------------------ */}

      {/* LA FENETRE D'ENTREE DU MODE. Elle explique ce que le mode change, et
          dit AUSSI par ou l'on sort : c'est la question qu'on se pose une fois
          dedans, et aucun menu ne peut y repondre en une ligne. Elle ne parait
          qu'a l'ENTREE.
          ELLE NE PARLE QUE DES FILTRES, ET C'EST UNE CORRECTION : elle annoncait
          "Ni filtre ni tri dedans", or le TRI reste entierement disponible et
          s'applique pour de vrai dans les archives (le moteur ne neutralise que
          les filtres). La phrase etait fausse sur la moitie de ce qu'elle
          annoncait, et elle dissuadait d'un geste qui marche. Le brief et le
          prototype ne parlent, eux non plus, que des filtres. */}
      <ConfirmDialog
        open={askArchivesMode}
        tone="neutral"
        title="Passer en mode archives ?"
        body={[
          `Les archives, et rien d'autre. ${ARCHIVES_FILTER_OFF_SHORT}`,
          'On en sort par la croix du tag.',
        ]}
        confirmLabel="Voir les archives"
        onCancel={() => setAskArchivesMode(false)}
        onConfirm={() => {
          setAskArchivesMode(false);
          tools?.setArchivesMode?.(true);
        }}
      />

      {/* LA FENETRE D'UNE ACTION QUI PORTE UN `confirm`. C'est le chassis qui
          l'ouvre et qui n'appelle `onClick` qu'apres : l'appelant ne gere
          aucune fenetre, donc il ne peut pas en oublier une.
          L'action est gardee en etat pendant le fondu de fermeture ; on rappelle
          `onClick` sur l'objet CLIQUE, c'est-a-dire l'etat que l'utilisateur a
          vu et confirme, jamais un objet recompose entre-temps. */}
      {confirming?.confirm != null && (
        <ConfirmDialog
          open={confirmOpen}
          tone={confirming.confirm.tone}
          title={confirming.confirm.title}
          body={confirming.confirm.body}
          confirmLabel={confirming.confirm.confirmLabel}
          {...(confirming.confirm.cancelLabel != null
            ? { cancelLabel: confirming.confirm.cancelLabel }
            : {})}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={() => {
            setConfirmOpen(false);
            confirming.onClick();
          }}
        />
      )}

      {/* ------------------------------------------------------------------
          LES FENETRES DU LOT (chantier 3_0), ELLES AUSSI A LA RACINE.
          Montees ICI et JAMAIS dans la rangee 3 : elle est `relative z-10` et
          ouvre un contexte d'empilement, un modal pose dedans passerait SOUS le
          contenu. Le piege a deja ete paye trois fois dans ce pack.

          CE QUI SE PASSE APRES L'ECRITURE N'EST PAS ECRIT ICI, ET C'EST VOULU :
          le chassis ne vide pas la selection a la fermeture. C'est le moteur qui
          recharge la porte ; l'invariant retire alors du lot les lignes qui ont
          quitte le jeu, et la barre se referme d'elle-meme si elle se vide. Un
          `clearSelection()` pose ici masquerait un echec partiel en donnant
          l'air d'un succes : les fiches TOMBEES doivent rester cochees.
          ------------------------------------------------------------------ */}

      {/* LA FENETRE "MODIFICATION GROUPEE". Elle ne se monte que si la porte
          sait ecrire une cellule ; sans ca, le bouton qui l'ouvre est deja
          eteint avec sa raison (voir `bulkAvailability` plus haut), donc il n'y
          a rien a ouvrir. Un champ modifiable manquant, lui, n'empeche PAS
          l'ouverture : c'est la fenetre qui le dit, elle ne s'ouvre pas vide.
          `onRunEnd` n'est pas branche ici non plus : meme raison qu'au
          `BulkRunner` plus bas, le moteur recharge deja de son cote. */}
      {writeCell != null && (
        <BulkEditDialog
          open={bulkEditOpen}
          onClose={() => setBulkEditOpen(false)}
          ids={bulkEdit?.ids ?? []}
          fields={tools?.bulkEditFields ?? []}
          writeCell={writeCell}
          {...(bulkEdit != null ? { labelOf: bulkEdit.labelOf } : {})}
        />
      )}

      {/* LES DEUX CONFIRMATIONS, CELLES DU CHANTIER 2_0, avec le NOMBRE. Meme
          boite, meme ton, meme ordre de boutons, meme focus par defaut sur
          Annuler : c'est le MEME geste que sur une fiche, il porte seulement un
          nombre. Rien de nouveau n'est ecrit ici, et rien ne doit l'etre. */}
      {bulkAsk?.kind === 'archive' && (
        <ArchiveConfirmDialog
          open={bulkAskOpen}
          count={bulkAsk.ids.length}
          onCancel={() => setBulkAskOpen(false)}
          onConfirm={() => {
            setBulkAskOpen(false);
            setBulkRun(bulkAsk);
            setBulkRunOpen(true);
          }}
        />
      )}
      {bulkAsk?.kind === 'remove' && (
        <RemoveConfirmDialog
          open={bulkAskOpen}
          count={bulkAsk.ids.length}
          onCancel={() => setBulkAskOpen(false)}
          onConfirm={() => {
            setBulkAskOpen(false);
            setBulkRun(bulkAsk);
            setBulkRunOpen(true);
          }}
        />
      )}

      {/* L'ECRITURE ELLE-MEME, une fiche a la fois. Le chassis ne BOUCLE PAS :
          il passe les ids et la primitive a document unique, et `BulkRunner`
          porte la progression ("4 sur 12"), le compte rendu, les fiches tombees
          NOMMEES et le rejeu DES ECHECS SEULEMENT. Un "Reessayer" qui rejouerait
          tout le lot reecrirait les fiches deja bonnes.

          `onRunEnd` N'EST PAS BRANCHE, ET C'EST UN CHOIX MOTIVE. Le rechargement
          de fin appartient au MOTEUR, qui le fait deja de son cote apres les
          ecritures, avec un rechargement qui GARDE la selection. Le `reload()`
          public, lui, la fait tomber : branche ici, il effacerait le lot au
          moment precis ou l'utilisateur en a le plus besoin, celui ou le compte
          rendu annonce "10 modifiees, 2 en echec" et ou les deux tombees
          doivent rester cochees pour etre rejouees. */}
      {bulkRun != null && (
        <BulkRunner
          open={bulkRunOpen}
          onClose={() => setBulkRunOpen(false)}
          ids={bulkRun.ids}
          verb={bulkRun.kind}
          runOne={bulkRun.runOne}
          labelOf={bulkRun.labelOf}
        />
      )}
    </div>
  );
}

export default ScreenLayout;
